<template>
  <div class="workspace-tab">
    <!-- Comp List View -->
    <div v-if="viewMode === 'list'" class="comp-list-view">
      <div class="comp-list-header">
        <div class="comp-header-info" style="display: flex; align-items: center; gap: 8px;">
          <h2 class="comp-header-title" v-if="selectedProject && selectedProject.brand">
            {{ selectedProject.brand.name }}: {{ selectedProject.name }}
          </h2>
          <h2 class="comp-header-title" v-else-if="selectedProject">
            {{ selectedProject.name }}
          </h2>
          <h2 class="comp-header-title" v-else>
            Comps
          </h2>
          <n-button
            v-if="selectedProject"
            size="small"
            type="primary"
            @click="handleCreateComp"
            title="Create new comp"
          >
            <template #icon>
              <n-icon>
                <svg viewBox="0 0 24 24">
                  <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                </svg>
              </n-icon>
            </template>
          </n-button>
        </div>
      </div>

      <!-- Two Column Layout: Comps on Left, Deliverables on Right -->
      <div class="workspace-two-column">
        <!-- Left Column: Comps -->
        <div class="comps-column">
          <div v-if="isLoadingComps" class="loading-state">
            <n-spin size="large" />
          </div>

          <div v-else-if="comps.length === 0" class="empty-state">
            <n-empty description="No comps yet. Create your first comp!">
              <template #icon>
                <n-icon size="48">
                  <svg viewBox="0 0 24 24">
                    <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                </n-icon>
              </template>
            </n-empty>
          </div>

          <div v-else class="comps-grid">
            <div
              v-for="comp in comps"
              :key="comp.id"
              class="comp-card"
            >
              <div class="comp-card-preview" @click="handleSelectComp(comp)">
                <div class="comp-preview-placeholder">
                  <span>{{ comp.width }}×{{ comp.height }}</span>
                </div>
              </div>
              <div class="comp-card-info">
                <n-input
                  v-if="editingCompId === comp.id"
                  v-model:value="comp.name"
                  @blur="handleCompNameUpdate(comp)"
                  @keyup.enter="handleCompNameUpdate(comp)"
                  @keyup.esc="editingCompId = null"
                  size="small"
                  class="comp-name-input"
                  autofocus
                />
                <h4
                  v-else
                  @click.stop="editingCompId = comp.id"
                  class="comp-name-editable"
                >
                  {{ comp.name }}
                </h4>
                <p class="comp-date">{{ formatDate(comp.createdAt) }}</p>
              </div>
              <div class="comp-card-actions">
                <n-button
                  size="small"
                  type="error"
                  text
                  @click.stop="handleDeleteComp(comp)"
                  title="Delete comp"
                >
                  <template #icon>
                    <n-icon>
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                      </svg>
                    </n-icon>
                  </template>
                </n-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Deliverables -->
        <div class="deliverables-column">
          <n-card class="deliverables-card">
            <template #header>
              <div class="deliverables-header" style="display: flex; align-items: center; justify-content: space-between;">
                <span>Deliverables</span>
                <n-button
                  v-if="selectedProject"
                  size="small"
                  type="primary"
                  text
                  @click="showCreateDeliverableDialog = true"
                  title="Create new deliverable"
                >
                  <template #icon>
                    <n-icon>
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                      </svg>
                    </n-icon>
                  </template>
                </n-button>
              </div>
            </template>

            <div v-if="isLoadingDeliverables" class="loading-state">
              <n-spin size="large" />
            </div>

            <div v-else-if="deliverables.length === 0" class="empty-state">
              <n-empty description="No deliverables yet. Publish a comp to create one!">
                <template #icon>
                  <n-icon size="48">
                    <svg viewBox="0 0 24 24">
                      <path fill="currentColor" d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z" />
                    </svg>
                  </n-icon>
                </template>
              </n-empty>
            </div>

            <div v-else class="deliverables-list">
              <div
                v-for="deliverable in deliverables"
                :key="deliverable.id"
                class="deliverable-item"
              >
                <h4 class="deliverable-title">{{ deliverable.name }}</h4>
                <p class="deliverable-meta">
                  <span v-if="deliverable.width && deliverable.height">{{ deliverable.width }}×{{ deliverable.height }}</span>
                  <span v-else>—</span>
                  <span> · </span>
                  <span>{{ formatDate(deliverable.createdAt) }}</span>
                  <span> · </span>
                  <span>v{{ deliverable.versionNumber }}</span>
                </p>
              </div>
            </div>
          </n-card>
        </div>
      </div>
    </div>

    <!-- Comp Edit View -->
    <div v-if="viewMode === 'edit'" class="workspace-main">
      <!-- Canvas Area (2/3 width) -->
      <div class="canvas-area">
        <div class="canvas-container" ref="canvasContainerRef">
          <canvas 
            ref="canvasRef"
            class="workspace-canvas"
            :width="canvasInternalWidth"
            :height="canvasInternalHeight"
            :style="canvasStyle"
            @drop="handleDrop"
            @dragover="handleDragOver"
            @mousedown="handleCanvasMouseDown"
            @mousemove="handleCanvasMouseMove"
            @mouseup="handleCanvasMouseUp"
            @mouseleave="handleCanvasMouseUp"
          ></canvas>
          <div class="canvas-overlay">
            <div class="canvas-info">
              <span>{{ aspectRatioDisplay }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Controls Panel (1/3 width) -->
      <div class="controls-panel">
        <n-card>
          <template #header>
            <div class="card-header">
              <span>{{ currentComp?.name || 'New Comp' }}</span>
              <div style="display: flex; gap: 8px;">
                <n-button quaternary size="small" @click="handleExportComp" title="Export Comp as JSON" v-if="currentComp">
                  <template #icon>
                    <n-icon>
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </n-icon>
                  </template>
                </n-button>
                <n-button quaternary size="small" @click="showPublishDialog = true" title="Publish" v-if="currentComp">
                  <template #icon>
                    <n-icon>
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z" />
                      </svg>
                    </n-icon>
                  </template>
                </n-button>
                <n-button quaternary size="small" @click="viewMode = 'list'" title="Close">
                  <template #icon>
                    <n-icon>
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </n-icon>
                  </template>
                </n-button>
              </div>
            </div>
          </template>

          <!-- Aspect Ratio Selection -->
          <div class="aspect-ratio-controls">
            <n-form-item label="Aspect Ratio">
              <n-button-group>
                <n-button
                  v-for="ratio in aspectRatioOptions"
                  :key="ratio.value"
                  :type="aspectRatio === ratio.value ? 'primary' : 'default'"
                  @click="aspectRatio = ratio.value"
                  :title="ratio.label"
                >
                  {{ ratio.label }}
                </n-button>
              </n-button-group>
            </n-form-item>
          </div>

          <!-- Layer Management -->
          <div class="layer-controls">
            <n-form-item label="Layer">
              <div class="layer-buttons">
                <n-button-group>
                  <n-button
                    v-for="layerType in existingLayerTypes"
                    :key="layerType"
                    :type="getLayerButtonType(layerType)"
                    @click="selectLayerByType(layerType)"
                    size="small"
                  >
                    {{ layerType }}
                  </n-button>
                  <n-dropdown
                    trigger="click"
                    :options="addLayerOptions"
                    @select="handleAddLayer"
                  >
                    <n-button size="small" type="default">
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </n-dropdown>
                </n-button-group>
              </div>
            </n-form-item>

            <!-- Layer Details -->
            <div class="layer-details">
              <!-- Image layers (BG and LOGO) -->
              <div v-if="selectedImageLayer" class="image-layer-details">
                <n-form-item :label="`${selectedImageLayer.name} Assets`">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                    <span></span>
                    <n-button 
                      size="small" 
                      type="primary" 
                      :disabled="!canRandomizeLayer(selectedLayer.id)"
                      @click="handleRandomizeLayer(selectedLayer.id)"
                      title="Randomize asset"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M12,6V9L16,5L12,1V4A8,8 0 0,0 4,12C4,13.57 4.46,15.03 5.24,16.26L6.7,14.8C6.25,13.97 6,13 6,12A6,6 0 0,1 12,6M18.76,7.74L17.3,9.2C17.74,10.04 18,11 18,12A6,6 0 0,1 12,18V15L8,19L12,23V20A8,8 0 0,0 20,12C20,10.43 19.54,8.97 18.76,7.74Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </div>
                  <div v-if="isLoadingAssets" class="loading-state-small">
                    <n-spin size="small" />
                  </div>
                  <div v-else-if="bgAssets.length === 0 && selectedImageLayer.name === 'BG'" class="empty-assets">
                    <p>No BG assets available</p>
                  </div>
                  <div v-else-if="logoAssets.length === 0 && selectedImageLayer.name === 'LOGO'" class="empty-assets">
                    <p>No LOGO assets available</p>
                  </div>
                  <div v-else-if="photoAssets.length === 0 && selectedImageLayer.name === 'PHOTO'" class="empty-assets">
                    <p>No PHOTO assets available</p>
                  </div>
                  <div v-else class="asset-grid-inline">
                    <div
                      v-for="asset in getAssetsForLayer(selectedImageLayer.name)"
                      :key="asset.id"
                      class="asset-item-inline"
                      :class="{ selected: selectedImageLayer.assetId === asset.id }"
                      @click="handleSelectAssetDirect(asset)"
                    >
                      <div class="asset-preview-inline">
                        <img
                          v-if="getAssetUrl(asset)"
                          :src="getAssetUrl(asset)"
                          :alt="asset.name"
                          @error="handleImageError"
                        />
                        <div v-else class="asset-placeholder-inline">
                          <n-icon size="24">
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                            </svg>
                          </n-icon>
                        </div>
                      </div>
                      <p class="asset-name-inline">{{ asset.name }}</p>
                    </div>
                  </div>
                </n-form-item>

              </div>

              <!-- Text layer -->
              <div v-if="selectedTextLayer" class="text-layer-details">
                <n-form-item label="Text Style">
                  <div style="display: flex; gap: 8px; align-items: center;">
                    <n-select
                      v-model:value="selectedTextStyleId"
                      :options="textStyleOptions"
                      placeholder="Select text style"
                      style="flex: 1; min-width: 120px;"
                      :dropdown-style="{ minWidth: '200px' }"
                      filterable
                      @update:value="handleTextStyleChange"
                    />
                    <n-button 
                      size="small" 
                      type="primary" 
                      :disabled="textStyleAssets.length === 0"
                      @click="handleRandomizeTextStyle"
                      title="Randomize text style"
                    >
                      <template #icon>
                        <n-icon>
                          <svg viewBox="0 0 24 24">
                            <path fill="currentColor" d="M12,6V9L16,5L12,1V4A8,8 0 0,0 4,12C4,13.57 4.46,15.03 5.24,16.26L6.7,14.8C6.25,13.97 6,13 6,12A6,6 0 0,1 12,6M18.76,7.74L17.3,9.2C17.74,10.04 18,11 18,12A6,6 0 0,1 12,18V15L8,19L12,23V20A8,8 0 0,0 20,12C20,10.43 19.54,8.97 18.76,7.74Z" />
                          </svg>
                        </n-icon>
                      </template>
                    </n-button>
                  </div>
                </n-form-item>
                <n-form-item label="Copy" class="copy-field">
                  <div style="display: flex; gap: 8px; align-items: flex-start;">
                    <n-input
                      v-model:value="selectedTextLayer.copy"
                      type="textarea"
                      :rows="4"
                      placeholder="Enter text copy..."
                      style="flex: 1;"
                    />
                    <div style="display: flex; flex-direction: column; gap: 4px;">
                      <n-button 
                        size="small" 
                        type="default" 
                        :disabled="!canCycleCopyPrev"
                        @click="handleCycleCopyPrev"
                        title="Previous copy"
                      >
                        <template #icon>
                          <n-icon>
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" />
                            </svg>
                          </n-icon>
                        </template>
                      </n-button>
                      <n-button 
                        size="small" 
                        type="primary" 
                        :disabled="textCopyAssets.length === 0"
                        @click="handleRandomizeTextCopy"
                        title="Randomize copy"
                      >
                        Aa
                      </n-button>
                      <n-button 
                        size="small" 
                        type="default" 
                        :disabled="!canCycleCopyNext"
                        @click="handleCycleCopyNext"
                        title="Next copy"
                      >
                        <template #icon>
                          <n-icon>
                            <svg viewBox="0 0 24 24">
                              <path fill="currentColor" d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
                            </svg>
                          </n-icon>
                        </template>
                      </n-button>
                    </div>
                  </div>
                </n-form-item>
                <n-form-item label="Alignment & Visible">
                  <div class="alignment-visible-controls">
                    <n-switch 
                      v-model:value="selectedLayer.visible" 
                      @update:value="toggleLayerVisibility"
                      size="small"
                    />
                  </div>
                </n-form-item>
                <n-form-item label="Scale Factor">
                  <n-input-number
                    v-model:value="textScaleFactor"
                    :min="0.1"
                    :max="10"
                    :step="0.1"
                    :precision="2"
                    :show-button="true"
                    style="width: 100%;"
                    @update:value="handleTextScaleChange"
                    @blur="handleScaleFactorBlur"
                  />
                </n-form-item>
                <n-form-item label="Position">
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                    <n-input-number
                      v-model:value="textPositionX"
                      :step="1"
                      placeholder="X"
                      @update:value="handleTextPositionXChange"
                    />
                    <n-input-number
                      v-model:value="textPositionY"
                      :step="1"
                      placeholder="Y"
                      @update:value="handleTextPositionYChange"
                    />
                  </div>
                </n-form-item>
                <n-form-item>
                  <n-button 
                    type="default" 
                    block
                    @click="handleResetTextLayer"
                  >
                    Reset (Scale: 1, Center)
                  </n-button>
                </n-form-item>
              </div>
            </div>
          </div>
        </n-card>
      </div>
    </div>

    <!-- Assets Manager Section - Always Visible -->
    <div class="assets-section">
      <AssetsManager
        :project-id="selectedProjectId"
        :selected-project="selectedProject"
        @asset-select="handleAssetsGridSelect"
      />
    </div>

    <!-- Asset Selector Modal -->
    <n-modal v-model:show="showAssetSelector" :mask-closable="false">
      <n-card
        style="width: 800px; max-height: 80vh;"
        title="Select Asset"
        :bordered="false"
        size="huge"
        role="dialog"
        aria-modal="true"
      >
        <div v-if="isLoadingAssets" class="loading-state">
          <n-spin size="large" />
        </div>
        <div v-else class="asset-selector-grid">
          <div
            v-for="asset in filteredAssets"
            :key="asset.id"
            class="asset-selector-item"
            :class="{ selected: selectedImageLayer?.assetId === asset.id }"
            @click="handleSelectAsset(asset)"
          >
            <div class="asset-selector-preview">
              <img
                v-if="getAssetUrl(asset)"
                :src="getAssetUrl(asset)"
                :alt="asset.name"
                @error="handleImageError"
              />
              <div v-else class="asset-placeholder-small">
                <n-icon size="32">
                  <svg viewBox="0 0 24 24">
                    <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                </n-icon>
              </div>
            </div>
            <p class="asset-selector-name">{{ asset.name }}</p>
          </div>
        </div>
        <template #footer>
          <div style="display: flex; justify-content: flex-end; gap: 12px">
            <n-button @click="showAssetSelector = false">Cancel</n-button>
            <n-button type="primary" @click="confirmAssetSelection" :disabled="!selectedImageLayer?.assetId">
              Select
            </n-button>
          </div>
        </template>
      </n-card>
    </n-modal>

    <!-- Publish Dialog -->
    <n-modal v-model:show="showPublishDialog" preset="dialog" title="Publish Comp" positive-text="Publish" negative-text="Cancel" @positive-click="handlePublishComp">
      <n-form-item label="Deliverable Name">
        <n-input v-model:value="publishDeliverableName" placeholder="e.g., INC25_social_carousel_a" />
      </n-form-item>
      <n-form-item label="Deliverable Type (Optional)">
        <n-input v-model:value="publishDeliverableType" placeholder="Optional type" />
      </n-form-item>
    </n-modal>
    <n-modal v-model:show="showCreateDeliverableDialog" preset="dialog" title="Create Deliverable" positive-text="Create" negative-text="Cancel" @positive-click="handleCreateDeliverable">
      <n-form-item label="Deliverable Name">
        <n-input v-model:value="createDeliverableName" placeholder="e.g., INC25_social_carousel_a" />
      </n-form-item>
      <n-form-item label="Deliverable Type (Optional)">
        <n-input v-model:value="createDeliverableType" placeholder="Optional type" />
      </n-form-item>
      <n-form-item label="Width">
        <n-input-number v-model:value="createDeliverableWidth" :min="1" :max="10000" />
      </n-form-item>
      <n-form-item label="Height">
        <n-input-number v-model:value="createDeliverableHeight" :min="1" :max="10000" />
      </n-form-item>
    </n-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useMessage, NIcon, NSpin, NEmpty, NModal, NCard, NFormItem, NInput, NInputNumber, NSelect, NButton, NButtonGroup } from 'naive-ui'
import { apiClient } from '@/services/api'
import AssetsManager from '@/components/AssetsManager.vue'

const message = useMessage()
const canvasRef = ref<HTMLCanvasElement>()
const canvasContainerRef = ref<HTMLDivElement>()

// Selection and drag state
const isDragging = ref(false)
const dragType = ref<'move' | 'resize' | null>(null)
const dragStartPos = ref({ x: 0, y: 0 })
const dragStartLayerState = ref({ x: 0, y: 0, width: 0, height: 0, fontSize: 0 })
const resizeHandle = ref<'nw' | 'ne' | 'sw' | 'se' | null>(null)

// View state
type ViewMode = 'list' | 'edit'
const viewMode = ref<ViewMode>('list')

// Comp state
const comps = ref<any[]>([])
const currentComp = ref<any>(null)
const isLoadingComps = ref(false)
const selectedProjectId = ref<string | null>(null)
const selectedProject = ref<any>(null)

// Comp editing state
const editingCompId = ref<string | null>(null)

// Deliverable state
const deliverables = ref<any[]>([])
const isLoadingDeliverables = ref(false)
const showPublishDialog = ref(false)
const publishDeliverableName = ref('')
const publishDeliverableType = ref('')
const showCreateDeliverableDialog = ref(false)
const createDeliverableName = ref('')
const createDeliverableType = ref('')
const createDeliverableWidth = ref(1080)
const createDeliverableHeight = ref(1080)

// Asset state
const assets = ref<any[]>([])
const isLoadingAssets = ref(false)
const showAssetSelector = ref(false)

// Aspect ratio state
type AspectRatio = '1x1' | '4x5' | '5x4' | '9x16' | '16x9'
const aspectRatio = ref<AspectRatio>('1x1')

const aspectRatioValues: Record<AspectRatio, { width: number; height: number }> = {
  '1x1': { width: 1, height: 1 },
  '4x5': { width: 4, height: 5 },
  '5x4': { width: 5, height: 4 },
  '9x16': { width: 9, height: 16 },
  '16x9': { width: 16, height: 9 }
}

// Base resolution for internal canvas rendering
const BASE_RESOLUTION = 1000

const backgroundColor = ref('#ffffff')

// Layer management
interface ImageLayer {
  id: string
  name: string
  type: 'image'
  visible: boolean
  opacity: number
  blendMode: string
  assetId: string | null
  assetUrl: string | null
  assetName: string | null
  assetImage: HTMLImageElement | null // Loaded image element
  x: number
  y: number
  width: number | null
  height: number | null
  rotation: number
  zIndex: number
}

interface TextLayer {
  id: string
  name: string
  type: 'text'
  visible: boolean
  opacity: number
  blendMode: string
  copy: string
  fontFamily: string
  fontSize: number
  fontWeight: string
  color: string
  textAlign: 'left' | 'center' | 'right'
  x: number
  y: number
  width?: number // Optional width for text bounds
  height?: number // Optional height for text bounds
  zIndex: number
}

type Layer = ImageLayer | TextLayer

const layers = ref<Layer[]>([
  {
    id: 'layer-1',
    name: 'BG',
    type: 'image',
    visible: true,
    opacity: 1,
    blendMode: 'normal',
    assetId: null,
    assetUrl: null,
    assetName: null,
    assetImage: null,
    x: 0,
    y: 0,
    width: null,
    height: null,
    rotation: 0,
    zIndex: 0
  },
  {
    id: 'layer-2',
    name: 'LOGO',
    type: 'image',
    visible: true,
    opacity: 1,
    blendMode: 'normal',
    assetId: null,
    assetUrl: null,
    assetName: null,
    assetImage: null,
    x: 0,
    y: 0,
    width: null,
    height: null,
    rotation: 0,
    zIndex: 1
  },
  {
    id: 'layer-3',
    name: 'TEXT',
    type: 'text',
    visible: true,
    opacity: 1,
    blendMode: 'normal',
    copy: '',
    fontFamily: 'Arial',
    fontSize: 24,
    fontWeight: '400',
    color: '#000000',
    textAlign: 'left',
    x: BASE_RESOLUTION / 2, // Center by default
    y: BASE_RESOLUTION / 2, // Center by default
    zIndex: 2
  }
])

const selectedLayerId = ref<string>('layer-3')

const selectedLayer = computed(() => {
  return layers.value.find(layer => layer.id === selectedLayerId.value) || layers.value[0]
})

const selectedImageLayer = computed(() => {
  const layer = selectedLayer.value
  return layer && layer.type === 'image' ? layer : null
})

const selectedTextLayer = computed(() => {
  const layer = selectedLayer.value
  return layer && layer.type === 'text' ? layer : null
})

// Get existing layer types from layers array
const existingLayerTypes = computed(() => {
  const types = new Set<string>()
  layers.value.forEach(layer => {
    types.add(layer.name)
  })
  // Return in a consistent order: BG, LOGO, TEXT, then others alphabetically
  const ordered = ['BG', 'LOGO', 'TEXT', 'PHOTO', 'STICKER', 'FRAME']
  const result: string[] = []
  ordered.forEach(type => {
    if (types.has(type)) {
      result.push(type)
      types.delete(type)
    }
  })
  // Add any remaining types alphabetically
  Array.from(types).sort().forEach(type => {
    result.push(type)
  })
  return result
})

// Get layer button type (primary if selected, default otherwise)
const getLayerButtonType = (layerType: string): 'primary' | 'default' => {
  const layer = layers.value.find(l => l.name === layerType)
  return layer && layer.id === selectedLayerId.value ? 'primary' : 'default'
}

// Select layer by type (selects first layer of that type)
const selectLayerByType = (layerType: string) => {
  const layer = layers.value.find(l => l.name === layerType)
  if (layer) {
    selectedLayerId.value = layer.id
    handleLayerChange(layer.id)
  }
}

// Add layer options for dropdown
const addLayerOptions = computed(() => {
  const availableTypes = ['photo', 'sticker', 'frame']
  return availableTypes.map(type => ({
    label: type.charAt(0).toUpperCase() + type.slice(1),
    value: type,
    key: type
  }))
})

// Handle adding a new layer
const handleAddLayer = async (layerType: string) => {
  if (!currentComp.value) {
    message.warning('No comp selected')
    return
  }
  
  try {
    // Find the highest zIndex
    const maxZIndex = layers.value.length > 0 
      ? Math.max(...layers.value.map(l => l.zIndex || 0))
      : -1
    
    // Create layer data - ensure all numeric values are proper floats
    const layerData: any = {
      label: layerType.toUpperCase(),
      key: `${layerType}-${Date.now()}`,
      type: layerType,
      zIndex: maxZIndex + 1,
      x: parseFloat((canvasInternalWidth.value / 2).toFixed(2)),
      y: parseFloat((canvasInternalHeight.value / 2).toFixed(2)),
      properties: JSON.stringify({}),
      transferMode: 'normal',
      visible: true,
      opacity: 1.0,
      rotation: 0.0,
    }
    
    // Set default properties based on layer type
    if (layerType === 'photo') {
      // Photo should be 90% of canvas on its longer side, maintaining aspect ratio
      const canvasWidth = canvasInternalWidth.value
      const canvasHeight = canvasInternalHeight.value
      const longerSide = Math.max(canvasWidth, canvasHeight)
      const defaultSize = longerSide * 0.9
      
      // Default photo aspect ratio (can be adjusted when image loads)
      const defaultAspectRatio = 4 / 3 // Common photo aspect ratio
      
      if (canvasWidth >= canvasHeight) {
        // Landscape canvas - width is longer
        layerData.width = parseFloat(defaultSize.toFixed(2))
        layerData.height = parseFloat((defaultSize / defaultAspectRatio).toFixed(2))
      } else {
        // Portrait canvas - height is longer
        layerData.width = parseFloat((defaultSize * defaultAspectRatio).toFixed(2))
        layerData.height = parseFloat(defaultSize.toFixed(2))
      }
      
      // Center the photo on canvas
      layerData.x = parseFloat(((canvasWidth - layerData.width) / 2).toFixed(2))
      layerData.y = parseFloat(((canvasHeight - layerData.height) / 2).toFixed(2))
      
      layerData.properties = JSON.stringify({
        type: layerType,
        imageUrl: '',
        imageId: null,
        scale: 1.0,
        maintainAspectRatio: true,
      })
    } else if (layerType === 'sticker') {
      layerData.width = parseFloat((200).toFixed(2))
      layerData.height = parseFloat((200).toFixed(2))
      layerData.properties = JSON.stringify({
        type: layerType,
        imageUrl: '',
        imageId: null,
        scale: 1.0,
        maintainAspectRatio: true,
      })
    } else if (layerType === 'text') {
      // For text layers, get random style and copy assets separately
      let styleAsset = null
      let copyAsset = null
      
      if (textStyleAssets.value.length > 0) {
        const randomStyleIndex = Math.floor(Math.random() * textStyleAssets.value.length)
        styleAsset = textStyleAssets.value[randomStyleIndex]
      }
      
      if (textCopyAssets.value.length > 0) {
        const randomCopyIndex = Math.floor(Math.random() * textCopyAssets.value.length)
        copyAsset = textCopyAssets.value[randomCopyIndex]
      }
      
      // Default text properties
      let textProps: any = {
        type: 'text',
        text: 'Sample Text',
        copy: 'Sample Text',
        fontFamily: 'Arial',
        fontSize: 24,
        fontWeight: '400',
        color: '#000000',
        textAlign: 'left',
        styleAssetId: styleAsset ? styleAsset.id : null,
        copyAssetId: copyAsset ? copyAsset.id : null,
        assetId: styleAsset ? styleAsset.id : null,
        imageId: styleAsset ? styleAsset.id : null,
      }
      
      // Apply text style asset if available
      if (styleAsset && styleAsset.metadata) {
        try {
          const metadata = typeof styleAsset.metadata === 'string' 
            ? JSON.parse(styleAsset.metadata) 
            : styleAsset.metadata
          
          if (metadata.fontFamily) textProps.fontFamily = metadata.fontFamily
          if (metadata.fontSize) textProps.fontSize = metadata.fontSize
          if (metadata.fontWeight) textProps.fontWeight = metadata.fontWeight
          if (metadata.color) textProps.color = metadata.color
          if (metadata.textAlign) textProps.textAlign = metadata.textAlign
        } catch (e) {
          console.error('Failed to parse text style asset metadata:', e)
        }
      }
      
      // Apply text copy asset if available
      if (copyAsset && copyAsset.metadata) {
        try {
          const metadata = typeof copyAsset.metadata === 'string' 
            ? JSON.parse(copyAsset.metadata) 
            : copyAsset.metadata
          
          // text-copy assets have an 'items' array - pick a random item
          if (metadata.items && Array.isArray(metadata.items) && metadata.items.length > 0) {
            const itemIndex = Math.floor(Math.random() * metadata.items.length)
            textProps.text = metadata.items[itemIndex]
            textProps.copy = metadata.items[itemIndex]
          } else if (metadata.text) {
            textProps.text = metadata.text
            textProps.copy = metadata.text
          } else if (metadata.sampleText) {
            textProps.text = metadata.sampleText
            textProps.copy = metadata.sampleText
          }
        } catch (e) {
          console.error('Failed to parse text copy asset metadata:', e)
        }
      }
      
      layerData.properties = JSON.stringify(textProps)
    } else if (layerType === 'frame') {
      layerData.width = parseFloat(canvasInternalWidth.value.toFixed(2))
      layerData.height = parseFloat(canvasInternalHeight.value.toFixed(2))
      layerData.properties = JSON.stringify({
        type: 'frame',
        frameType: 'image',
        imageUrl: '',
        imageId: null,
        borderWidth: 0,
        borderRadius: 0,
      })
    }
    
    const response = await apiClient.createCompLayer(currentComp.value.id, layerData)
    
    // Reload comp to get the new layer
    const compResponse = await apiClient.getComp(currentComp.value.id)
    currentComp.value = compResponse.comp
    
    // Update layers
    if (compResponse.comp.layers_rel && compResponse.comp.layers_rel.length > 0) {
      layers.value = compResponse.comp.layers_rel.map((layer: any) => {
        const props = layer.properties ? JSON.parse(layer.properties) : {}
        if (layer.type === 'text') {
          return {
            id: layer.id,
            name: layer.label,
            type: 'text' as const,
            visible: layer.visible,
            opacity: layer.opacity,
            blendMode: layer.transferMode || 'normal',
            copy: props.text || props.copy || '',
            fontFamily: props.fontFamily || 'Arial',
            fontSize: props.fontSize || 24,
            fontWeight: props.fontWeight || '400',
            color: props.color || '#000000',
            textAlign: props.textAlign || 'left',
            x: layer.x,
            y: layer.y,
            zIndex: layer.zIndex || 0
          }
        } else {
          const imageLayer = {
            id: layer.id,
            name: layer.label,
            type: 'image' as const,
            visible: layer.visible,
            opacity: layer.opacity,
            blendMode: layer.transferMode || 'normal',
            assetId: props.imageId || props.assetId || layer.srcImage || null,
            assetUrl: props.imageUrl || null,
            assetName: null,
            assetImage: null,
            x: layer.x,
            y: layer.y,
            width: layer.width || null,
            height: layer.height || null,
            rotation: layer.rotation || 0,
            zIndex: layer.zIndex || 0
          }
          
          if (imageLayer.assetId) {
            const asset = assets.value.find(a => a.id === imageLayer.assetId)
            if (asset) {
              try {
                if (asset.metadata) {
                  const meta = JSON.parse(asset.metadata)
                  imageLayer.assetUrl = meta.url || meta.previewUrl || meta.iconUrl || null
                  if (imageLayer.assetUrl) {
                    loadAssetImage(imageLayer)
                  }
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          } else if (imageLayer.assetUrl) {
            loadAssetImage(imageLayer)
          }
          
          return imageLayer
        }
      })
    }
    
    // Select the newly created layer
    const newLayer = layers.value.find(l => l.id === response.layer.id)
    if (newLayer) {
      selectedLayerId.value = newLayer.id
    }
    
    message.success('Layer added')
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to add layer:', error)
    message.error(error?.message || 'Failed to add layer')
  }
}

// Text style assets (for styling)
const textStyleAssets = computed(() => {
  return assets.value.filter(a => a.type === 'text-style')
})

// Text copy assets (for copy content)
const textCopyAssets = computed(() => {
  return assets.value.filter(a => a.type === 'text-copy')
})

// Legacy: all text assets (for backward compatibility)
const textAssets = computed(() => {
  return [...textStyleAssets.value, ...textCopyAssets.value]
})

const textStyleOptions = computed(() => {
  return textStyleAssets.value.map(asset => ({
    label: asset.name,
    value: asset.id
  }))
})

const selectedTextStyleId = ref<string | null>(null)
const selectedTextCopyId = ref<string | null>(null)
const selectedTextCopyIndex = ref<number>(0) // Index in the current copy asset's items array

// Handle text style change from dropdown
const handleTextStyleChange = (assetId: string | null) => {
  if (!assetId || !selectedTextLayer.value) return
  
  const textAsset = textStyleAssets.value.find(a => a.id === assetId)
  if (!textAsset) return
  
  // Parse metadata to get style properties
  let styleProps: any = {
    fontFamily: 'Arial',
    fontSize: 24,
    fontWeight: '400',
    color: '#000000',
    textAlign: 'left' // Default to left alignment
  }
  
  if (textAsset.metadata) {
    try {
      const metadata = typeof textAsset.metadata === 'string' 
        ? JSON.parse(textAsset.metadata) 
        : textAsset.metadata
      
      if (metadata.fontFamily) styleProps.fontFamily = metadata.fontFamily
      if (metadata.fontSize) styleProps.fontSize = metadata.fontSize
      if (metadata.fontWeight) styleProps.fontWeight = metadata.fontWeight
      if (metadata.color) styleProps.color = metadata.color
      if (metadata.textAlign) styleProps.textAlign = metadata.textAlign
    } catch (e) {
      console.error('Failed to parse text asset metadata:', e)
    }
  }
  
  // Apply style to text layer
  // Preserve scale factor and position when changing style
  if (selectedTextLayer.value) {
    const currentFontSize = selectedTextLayer.value.fontSize
    const currentX = selectedTextLayer.value.x
    const currentY = selectedTextLayer.value.y
    
    // Get current style's base fontSize to calculate scale factor
    let currentBaseFontSize = BASE_FONT_SIZE
    if (selectedTextStyleId.value) {
      const currentStyleAsset = textStyleAssets.value.find(a => a.id === selectedTextStyleId.value)
      if (currentStyleAsset?.metadata) {
        try {
          const currentMetadata = typeof currentStyleAsset.metadata === 'string' 
            ? JSON.parse(currentStyleAsset.metadata) 
            : currentStyleAsset.metadata
          if (currentMetadata.fontSize) {
            currentBaseFontSize = currentMetadata.fontSize
          }
        } catch (e) {
          // Use default
        }
      }
    }
    
    // Calculate scale factor relative to current base fontSize
    const scaleFactor = currentFontSize / currentBaseFontSize
    
    // Get new style's base fontSize
    const newBaseFontSize = styleProps.fontSize || BASE_FONT_SIZE
    
    // Apply scale factor to new base fontSize
    const newFontSize = newBaseFontSize * scaleFactor
    
    selectedTextLayer.value.fontFamily = styleProps.fontFamily
    selectedTextLayer.value.fontSize = newFontSize
    selectedTextLayer.value.fontWeight = styleProps.fontWeight
    selectedTextLayer.value.color = styleProps.color
    selectedTextLayer.value.textAlign = styleProps.textAlign || 'left'
    
    // Preserve position
    selectedTextLayer.value.x = currentX
    selectedTextLayer.value.y = currentY
    
    // Don't update copy when changing style - style assets are for styling only
    // Copy should be managed separately via copy assets
  }
  
  nextTick(() => {
    renderCanvas()
  })
}

// Randomize text style only
const handleRandomizeTextStyle = async () => {
  if (!currentComp.value || !selectedTextLayer.value) {
    message.warning('No text layer selected')
    return
  }
  
  if (textStyleAssets.value.length === 0) {
    message.warning('No text style assets available')
    return
  }
  
  try {
    // Pick a random text style asset
    const randomIndex = Math.floor(Math.random() * textStyleAssets.value.length)
    const randomStyleAsset = textStyleAssets.value[randomIndex]
    
    // Apply style from asset
    // Preserve scale factor and position when randomizing style
    const currentFontSize = selectedTextLayer.value.fontSize
    const currentX = selectedTextLayer.value.x
    const currentY = selectedTextLayer.value.y
    
    // Get current style's base fontSize to calculate scale factor
    let currentBaseFontSize = BASE_FONT_SIZE
    if (selectedTextStyleId.value) {
      const currentStyleAsset = textStyleAssets.value.find(a => a.id === selectedTextStyleId.value)
      if (currentStyleAsset?.metadata) {
        try {
          const currentMetadata = typeof currentStyleAsset.metadata === 'string' 
            ? JSON.parse(currentStyleAsset.metadata) 
            : currentStyleAsset.metadata
          if (currentMetadata.fontSize) {
            currentBaseFontSize = currentMetadata.fontSize
          }
        } catch (e) {
          // Use default
        }
      }
    }
    
    // Calculate scale factor relative to current base fontSize
    const scaleFactor = currentFontSize / currentBaseFontSize
    
    if (randomStyleAsset.metadata) {
      try {
        const metadata = typeof randomStyleAsset.metadata === 'string' 
          ? JSON.parse(randomStyleAsset.metadata) 
          : randomStyleAsset.metadata
        
        // Get new style's base fontSize
        const newBaseFontSize = metadata.fontSize || BASE_FONT_SIZE
        
        // Apply scale factor to new base fontSize
        const newFontSize = newBaseFontSize * scaleFactor
        
        if (metadata.fontFamily) selectedTextLayer.value.fontFamily = metadata.fontFamily
        selectedTextLayer.value.fontSize = newFontSize
        if (metadata.fontWeight) selectedTextLayer.value.fontWeight = metadata.fontWeight
        if (metadata.color) selectedTextLayer.value.color = metadata.color
        if (metadata.textAlign) selectedTextLayer.value.textAlign = metadata.textAlign
        
        // Preserve position
        selectedTextLayer.value.x = currentX
        selectedTextLayer.value.y = currentY
        
        // Update selected style ID
        selectedTextStyleId.value = randomStyleAsset.id
        
        // Get current properties to preserve copy
        const currentProps = selectedTextLayer.value.copy ? {
          text: selectedTextLayer.value.copy,
          copy: selectedTextLayer.value.copy,
        } : {}
        
        // Save to backend
        await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
          x: currentX,
          y: currentY,
          properties: JSON.stringify({
            type: 'text',
            ...currentProps,
            fontFamily: selectedTextLayer.value.fontFamily,
            fontSize: newFontSize,
            fontWeight: selectedTextLayer.value.fontWeight,
            color: selectedTextLayer.value.color,
            textAlign: selectedTextLayer.value.textAlign,
            styleAssetId: randomStyleAsset.id,
            assetId: randomStyleAsset.id,
            imageId: randomStyleAsset.id,
          })
        })
        
        message.success('Text style randomized')
        nextTick(() => {
          renderCanvas()
        })
      } catch (e) {
        console.error('Failed to parse text style asset metadata:', e)
        message.error('Failed to randomize text style')
      }
    }
  } catch (error: any) {
    console.error('Failed to randomize text style:', error)
    message.error(error?.message || 'Failed to randomize text style')
  }
}

// Randomize text copy only
const handleRandomizeTextCopy = async () => {
  if (!currentComp.value || !selectedTextLayer.value) {
    message.warning('No text layer selected')
    return
  }
  
  if (textCopyAssets.value.length === 0) {
    message.warning('No text copy assets available')
    return
  }
  
  try {
    // Pick a random text copy asset
    const randomIndex = Math.floor(Math.random() * textCopyAssets.value.length)
    const randomCopyAsset = textCopyAssets.value[randomIndex]
    
    // Get copy from asset metadata
    let newCopy = 'Sample Text'
    let itemIndex = 0
    
    if (randomCopyAsset.metadata) {
      try {
        const metadata = typeof randomCopyAsset.metadata === 'string' 
          ? JSON.parse(randomCopyAsset.metadata) 
          : randomCopyAsset.metadata
        
        // text-copy assets have an 'items' array - pick a random item
        if (metadata.items && Array.isArray(metadata.items) && metadata.items.length > 0) {
          itemIndex = Math.floor(Math.random() * metadata.items.length)
          newCopy = metadata.items[itemIndex]
        } else if (metadata.text) {
          newCopy = metadata.text
        } else if (metadata.sampleText) {
          newCopy = metadata.sampleText
        }
      } catch (e) {
        console.error('Failed to parse text copy asset metadata:', e)
      }
    }
    
    // Update copy and track which asset/index we're using
    selectedTextLayer.value.copy = newCopy
    selectedTextCopyId.value = randomCopyAsset.id
    selectedTextCopyIndex.value = itemIndex
    
    // Save to backend
    await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
      properties: JSON.stringify({
        type: 'text',
        text: newCopy,
        copy: newCopy,
        fontFamily: selectedTextLayer.value.fontFamily,
        fontSize: selectedTextLayer.value.fontSize,
        fontWeight: selectedTextLayer.value.fontWeight,
        color: selectedTextLayer.value.color,
        textAlign: selectedTextLayer.value.textAlign,
        copyAssetId: randomCopyAsset.id,
        copyItemIndex: itemIndex,
        styleAssetId: selectedTextStyleId.value,
        assetId: selectedTextStyleId.value,
        imageId: selectedTextStyleId.value,
      })
    })
    
    message.success('Text copy randomized')
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to randomize text copy:', error)
    message.error(error?.message || 'Failed to randomize text copy')
  }
}

// Get current copy asset and items
const getCurrentCopyAsset = () => {
  if (!selectedTextCopyId.value) return null
  return textCopyAssets.value.find(a => a.id === selectedTextCopyId.value)
}

const getCurrentCopyItems = (): string[] => {
  const asset = getCurrentCopyAsset()
  if (!asset || !asset.metadata) return []
  
  try {
    const metadata = typeof asset.metadata === 'string' 
      ? JSON.parse(asset.metadata) 
      : asset.metadata
    
    if (metadata.items && Array.isArray(metadata.items) && metadata.items.length > 0) {
      return metadata.items
    } else if (metadata.text) {
      return [metadata.text]
    } else if (metadata.sampleText) {
      return [metadata.sampleText]
    }
  } catch (e) {
    console.error('Failed to parse copy asset metadata:', e)
  }
  
  return []
}

// Computed properties for cycle buttons
const canCycleCopyPrev = computed(() => {
  if (!selectedTextCopyId.value || !selectedTextLayer.value) return false
  const items = getCurrentCopyItems()
  return items.length > 0 && selectedTextCopyIndex.value > 0
})

const canCycleCopyNext = computed(() => {
  if (!selectedTextCopyId.value || !selectedTextLayer.value) return false
  const items = getCurrentCopyItems()
  return items.length > 0 && selectedTextCopyIndex.value < items.length - 1
})

// Cycle to previous copy item
const handleCycleCopyPrev = async () => {
  if (!currentComp.value || !selectedTextLayer.value || !canCycleCopyPrev.value) return
  
  const items = getCurrentCopyItems()
  if (items.length === 0) return
  
  selectedTextCopyIndex.value = Math.max(0, selectedTextCopyIndex.value - 1)
  const newCopy = items[selectedTextCopyIndex.value]
  
  selectedTextLayer.value.copy = newCopy
  
  // Save to backend
  try {
    await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
      properties: JSON.stringify({
        type: 'text',
        text: newCopy,
        copy: newCopy,
        fontFamily: selectedTextLayer.value.fontFamily,
        fontSize: selectedTextLayer.value.fontSize,
        fontWeight: selectedTextLayer.value.fontWeight,
        color: selectedTextLayer.value.color,
        textAlign: selectedTextLayer.value.textAlign,
        copyAssetId: selectedTextCopyId.value,
        copyItemIndex: selectedTextCopyIndex.value,
        styleAssetId: selectedTextStyleId.value,
        assetId: selectedTextStyleId.value,
        imageId: selectedTextStyleId.value,
      })
    })
    
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to cycle copy:', error)
    message.error(error?.message || 'Failed to cycle copy')
  }
}

// Cycle to next copy item
const handleCycleCopyNext = async () => {
  if (!currentComp.value || !selectedTextLayer.value || !canCycleCopyNext.value) return
  
  const items = getCurrentCopyItems()
  if (items.length === 0) return
  
  selectedTextCopyIndex.value = Math.min(items.length - 1, selectedTextCopyIndex.value + 1)
  const newCopy = items[selectedTextCopyIndex.value]
  
  selectedTextLayer.value.copy = newCopy
  
  // Save to backend
  try {
    await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
      properties: JSON.stringify({
        type: 'text',
        text: newCopy,
        copy: newCopy,
        fontFamily: selectedTextLayer.value.fontFamily,
        fontSize: selectedTextLayer.value.fontSize,
        fontWeight: selectedTextLayer.value.fontWeight,
        color: selectedTextLayer.value.color,
        textAlign: selectedTextLayer.value.textAlign,
        copyAssetId: selectedTextCopyId.value,
        copyItemIndex: selectedTextCopyIndex.value,
        styleAssetId: selectedTextStyleId.value,
        assetId: selectedTextStyleId.value,
        imageId: selectedTextStyleId.value,
      })
    })
    
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to cycle copy:', error)
    message.error(error?.message || 'Failed to cycle copy')
  }
}

// Aspect ratio options for buttons
const aspectRatioOptions = [
  { label: '9:16', value: '9x16' as AspectRatio },
  { label: '4:5', value: '4x5' as AspectRatio },
  { label: '1:1', value: '1x1' as AspectRatio },
  { label: '5:4', value: '5x4' as AspectRatio },
  { label: '16:9', value: '16x9' as AspectRatio }
]

// Canvas dimensions
const containerSize = ref({ width: 0, height: 0 })

const canvasInternalWidth = computed(() => {
  const ratio = aspectRatioValues[aspectRatio.value]
  const aspect = ratio.width / ratio.height
  if (aspect >= 1) {
    return BASE_RESOLUTION
  } else {
    return BASE_RESOLUTION * aspect
  }
})

const canvasInternalHeight = computed(() => {
  const ratio = aspectRatioValues[aspectRatio.value]
  const aspect = ratio.width / ratio.height
  if (aspect >= 1) {
    return BASE_RESOLUTION / aspect
  } else {
    return BASE_RESOLUTION
  }
})

const canvasDisplayDimensions = computed(() => {
  if (!containerSize.value.width || !containerSize.value.height) {
    return { width: 0, height: 0 }
  }

  const ratio = aspectRatioValues[aspectRatio.value]
  const containerAspect = containerSize.value.width / containerSize.value.height
  const canvasAspect = ratio.width / ratio.height

  let width: number
  let height: number

  if (canvasAspect > containerAspect) {
    // Canvas is wider - fit to width
    width = containerSize.value.width
    height = width / canvasAspect
  } else {
    // Canvas is taller - fit to height
    height = containerSize.value.height
    width = height * canvasAspect
  }

  return { width, height }
})

const canvasStyle = computed(() => {
  const dims = canvasDisplayDimensions.value
  return {
    width: `${dims.width}px`,
    height: `${dims.height}px`
  }
})

const aspectRatioDisplay = computed(() => {
  const ratio = aspectRatioValues[aspectRatio.value]
  return `${ratio.width}:${ratio.height}`
})

// Asset filtering
const bgAssets = computed(() => {
  return assets.value.filter(a => a.type === 'bg')
})

const logoAssets = computed(() => {
  return assets.value.filter(a => a.type === 'logo')
})

const photoAssets = computed(() => {
  return assets.value.filter(a => a.type === 'photo')
})

// Get assets for a specific layer type
const getAssetsForLayer = (layerName: string) => {
  const layerType = layerName.toLowerCase()
  if (layerType === 'bg') return bgAssets.value
  if (layerType === 'logo') return logoAssets.value
  if (layerType === 'photo') return photoAssets.value
  return []
}

const filteredAssets = computed(() => {
  if (!selectedImageLayer.value) return []
  
  // Filter by layer type (bg, logo, photo)
  const layerType = selectedImageLayer.value.name.toLowerCase()
  let assetType: string | null = null
  
  if (layerType === 'bg') assetType = 'bg'
  else if (layerType === 'logo') assetType = 'logo'
  else if (layerType === 'photo') assetType = 'photo'
  
  if (assetType) {
    return assets.value.filter(a => a.type === assetType)
  }
  
  return assets.value
})

// Load comps
const loadComps = async () => {
  if (!selectedProjectId.value) {
    comps.value = []
    return
  }
  
  isLoadingComps.value = true
  try {
    const response = await apiClient.getComps({
      projectId: selectedProjectId.value,
      limit: 100
    })
    comps.value = response.comps
  } catch (error) {
    console.error('Failed to load comps:', error)
    message.error('Failed to load comps')
  } finally {
    isLoadingComps.value = false
  }
}

// Load deliverables
const loadDeliverables = async () => {
  if (!selectedProjectId.value) {
    deliverables.value = []
    return
  }
  
  isLoadingDeliverables.value = true
  try {
    const response = await apiClient.getDeliverables({
      projectId: selectedProjectId.value,
      limit: 100
    })
    deliverables.value = response.deliverables
  } catch (error) {
    console.error('Failed to load deliverables:', error)
    message.error('Failed to load deliverables')
  } finally {
    isLoadingDeliverables.value = false
  }
}

// Publish comp to deliverable
const handlePublishComp = async () => {
  if (!currentComp.value || !selectedProjectId.value) return
  
  if (!publishDeliverableName.value.trim()) {
    message.error('Deliverable name is required')
    return
  }
  
  try {
    await apiClient.publishComp(
      currentComp.value.id,
      publishDeliverableName.value.trim(),
      selectedProjectId.value,
      publishDeliverableType.value || undefined
    )
    message.success('Comp published successfully')
    showPublishDialog.value = false
    publishDeliverableName.value = ''
    publishDeliverableType.value = ''
    await loadDeliverables()
  } catch (error: any) {
    console.error('Failed to publish comp:', error)
    message.error(error.response?.data?.error || 'Failed to publish comp')
  }
}

// Load assets
const loadAssets = async () => {
  if (!selectedProjectId.value) return
  
  isLoadingAssets.value = true
  try {
    const response = await apiClient.getAssets({
      projectId: selectedProjectId.value,
      limit: 1000
    })
    assets.value = response.assets
  } catch (error) {
    console.error('Failed to load assets:', error)
  } finally {
    isLoadingAssets.value = false
  }
}

// Generate comp name with next available letter (unused - kept for future use)
// const generateCompName = async (): Promise<string> => {
//   if (!selectedProjectId.value || !selectedProject.value) {
//     return 'New Comp'
//   }
//   
//   const brandCode = selectedProject.value.brand?.code || 'BRAND'
//   const projectCode = selectedProject.value.code || 'PROJECT'
//   const baseName = `${brandCode}_${projectCode}_post_`
//   
//   // Get existing comps with this pattern
//   const existingComps = comps.value.filter(c => c.name.startsWith(baseName))
//   
//   // Extract letters from existing comps
//   const usedLetters = new Set<string>()
//   existingComps.forEach(comp => {
//     const match = comp.name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([a-z])$`))
//     if (match && match[1]) {
//       usedLetters.add(match[1])
//     }
//   })
//   
//   // Find next available letter
//   let nextLetter = 'a'
//   for (let i = 0; i < 26; i++) {
//     const letter = String.fromCharCode(97 + i) // 'a' to 'z'
//     if (!usedLetters.has(letter)) {
//       nextLetter = letter
//       break
//     }
//   }
//   
//   return `${baseName}${nextLetter}`
// }

// Load project details
const loadProject = async () => {
  if (!selectedProjectId.value) return
  try {
    const response = await apiClient.getProject(selectedProjectId.value)
    selectedProject.value = response.project
  } catch (error) {
    console.error('Failed to load project:', error)
  }
}

// Create comp
const handleCreateComp = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project first')
    return
  }
  
  try {
    // Load project if not loaded
    if (!selectedProject.value) {
      await loadProject()
    }
    
    // Generate a simple comp name
    const brandCode = selectedProject.value?.brand?.code || 'BRAND'
    const projectCode = selectedProject.value?.code || 'PROJECT'
    const compName = `${brandCode}_${projectCode}_comp_${Date.now()}`
    
    // Create comp with default dimensions
    const compData: any = {
      name: compName,
      width: 1080,
      height: 1080,
      projectId: selectedProjectId.value
    }
    
    const response = await apiClient.createComp(compData)
    // Reload the comp to get auto-created layers with database IDs
    const compResponse = await apiClient.getComp(response.comp.id)
    currentComp.value = compResponse.comp
    await loadComps()
    
    // Load layers from the comp (with database IDs)
    if (compResponse.comp.layers_rel && compResponse.comp.layers_rel.length > 0) {
      layers.value = compResponse.comp.layers_rel.map((layer: any) => {
        const props = layer.properties ? JSON.parse(layer.properties) : {}
        if (layer.type === 'text') {
          return {
            id: layer.id,
            name: layer.label,
            type: 'text' as const,
            visible: layer.visible,
            opacity: layer.opacity,
            blendMode: layer.transferMode || 'normal',
            copy: props.text || props.copy || '',
            fontFamily: props.fontFamily || 'Arial',
            fontSize: props.fontSize || 24,
            fontWeight: props.fontWeight || '400',
            color: props.color || '#000000',
            textAlign: props.textAlign || 'left',
            x: layer.x,
            y: layer.y,
            zIndex: layer.zIndex || 0
          }
        } else {
          const imageLayer = {
            id: layer.id,
            name: layer.label,
            type: 'image' as const,
            visible: layer.visible,
            opacity: layer.opacity,
            blendMode: layer.transferMode || 'normal',
            assetId: props.imageId || props.assetId || layer.srcImage || null,
            assetUrl: props.imageUrl || null,
            assetName: null,
            assetImage: null,
            x: layer.x,
            y: layer.y,
            width: layer.width || null,
            height: layer.height || null,
            rotation: layer.rotation || 0,
            zIndex: layer.zIndex || 0
          }
          
          // Load asset URL if assetId exists
          if (imageLayer.assetId) {
            const asset = assets.value.find(a => a.id === imageLayer.assetId)
            if (asset) {
              try {
                if (asset.metadata) {
                  const meta = JSON.parse(asset.metadata)
                  imageLayer.assetUrl = meta.url || meta.previewUrl || meta.iconUrl || null
                  if (imageLayer.assetUrl) {
                    loadAssetImage(imageLayer)
                  }
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          } else if (imageLayer.assetUrl) {
            loadAssetImage(imageLayer)
          }
          
          return imageLayer
        }
      })
    }
    
    viewMode.value = 'edit'
    message.success('Comp created')
  } catch (error: any) {
    console.error('Failed to create comp:', error)
    message.error(error?.message || 'Failed to create comp')
  }
}

// Create deliverable
const handleCreateDeliverable = async () => {
  if (!selectedProjectId.value) {
    message.warning('Please select a project first')
    return false
  }
  
  if (!createDeliverableName.value.trim()) {
    message.error('Deliverable name is required')
    return false
  }
  
  if (!createDeliverableWidth.value || !createDeliverableHeight.value) {
    message.error('Width and height are required')
    return false
  }
  
  try {
    // Create a version first (required for deliverable)
    const versionData = {
      name: createDeliverableName.value.trim(),
      width: createDeliverableWidth.value,
      height: createDeliverableHeight.value,
      layers: JSON.stringify([]), // Empty layers for now
      projectId: selectedProjectId.value,
      source: 'workspace' as const
    }
    
    const versionResponse = await apiClient.createVersion(versionData)
    
    // Create deliverable with the version
    const deliverableData = {
      projectId: selectedProjectId.value,
      versionId: versionResponse.version.id,
      name: createDeliverableName.value.trim(),
      type: createDeliverableType.value || undefined,
      width: createDeliverableWidth.value,
      height: createDeliverableHeight.value
    }
    
    await apiClient.createDeliverable(deliverableData)
    
    message.success('Deliverable created successfully')
    showCreateDeliverableDialog.value = false
    createDeliverableName.value = ''
    createDeliverableType.value = ''
    createDeliverableWidth.value = 1080
    createDeliverableHeight.value = 1080
    await loadDeliverables()
    return true
  } catch (error: any) {
    console.error('Failed to create deliverable:', error)
    message.error(error?.message || 'Failed to create deliverable')
    return false
  }
}

// Old commented out function:
// const handleCreateComp = async () => {
//   if (!selectedProjectId.value) {
//     message.warning('Please select a project first')
//     return
//   }
//   
//   try {
//     // Load project if not loaded
//     if (!selectedProject.value) {
//       await loadProject()
//     }
//     
//     // Ensure assets are loaded first
//     if (!assets.value.length) {
//       await loadAssets()
//     }
//     
//     // Generate comp name
//     const compName = await generateCompName()
//     
//     // Try to get default comp template
//     let defaultTemplate = null
//     try {
//       const templateResponse = await apiClient.getCompTemplates({ isDefault: true })
//       if (templateResponse.templates && templateResponse.templates.length > 0) {
//         defaultTemplate = templateResponse.templates[0]
//       }
//     } catch (error) {
//       // If template fetch fails, continue without template
//       console.warn('Failed to load default template:', error)
//     }
//     
//     // Create comp with template data if available
//     const compData: any = {
//       name: compName,
//       width: defaultTemplate?.width || 1080,
//       height: defaultTemplate?.height || 1080,
//       projectId: selectedProjectId.value
//     }
//     
//     // Add template layers if available
//     if (defaultTemplate?.compData?.layers) {
//       compData.layers = defaultTemplate.compData.layers
//       if (defaultTemplate.compData.backgroundColor) {
//         compData.backgroundColor = defaultTemplate.compData.backgroundColor
//       }
//     }
//     
//     const response = await apiClient.createComp(compData)
//     // Reload the comp to get auto-created layers with database IDs
//     const compResponse = await apiClient.getComp(response.comp.id)
//     currentComp.value = compResponse.comp
//     await loadComps()
//     
//     // Load layers from the comp (with database IDs)
//     if (compResponse.comp.layers_rel && compResponse.comp.layers_rel.length > 0) {
//       layers.value = compResponse.comp.layers_rel.map((layer: any) => {
//         const props = layer.properties ? JSON.parse(layer.properties) : {}
//         if (layer.type === 'text') {
//           return {
//             id: layer.id, // Use database ID
//             name: layer.label,
//             type: 'text' as const,
//             visible: layer.visible,
//             opacity: layer.opacity,
//             blendMode: layer.transferMode || 'normal',
//             copy: props.text || props.copy || '',
//             fontFamily: props.fontFamily || 'Arial',
//             fontSize: props.fontSize || 24,
//             fontWeight: props.fontWeight || '400',
//             color: props.color || '#000000',
//             textAlign: props.textAlign || 'left',
//             x: layer.x,
//             y: layer.y,
//             zIndex: layer.zIndex || 0
//           }
//         } else {
//           const imageLayer = {
//             id: layer.id, // Use database ID
//             name: layer.label,
//             type: 'image' as const,
//             visible: layer.visible,
//             opacity: layer.opacity,
//             blendMode: layer.transferMode || 'normal',
//             assetId: props.imageId || props.assetId || layer.srcImage || null,
//             assetUrl: props.imageUrl || null,
//             assetName: null,
//             assetImage: null,
//             x: layer.x,
//             y: layer.y,
//             width: layer.width || null,
//             height: layer.height || null,
//             rotation: layer.rotation || 0,
//             zIndex: layer.zIndex || 0
//           }
//           
//           // Load asset URL if assetId exists
//           if (imageLayer.assetId) {
//             const asset = assets.value.find(a => a.id === imageLayer.assetId)
//             if (asset) {
//               try {
//                 if (asset.metadata) {
//                   const meta = JSON.parse(asset.metadata)
//                   imageLayer.assetUrl = meta.url || meta.previewUrl || meta.iconUrl || null
//                   if (imageLayer.assetUrl) {
//                     loadAssetImage(imageLayer)
//                   }
//                 }
//               } catch (e) {
//                 // Ignore parse errors
//               }
//             }
//           } else if (imageLayer.assetUrl) {
//             loadAssetImage(imageLayer)
//           }
//           
//           return imageLayer
//         }
//       })
//     }
//     
//     viewMode.value = 'edit'
//     message.success('Comp created')
//   } catch (error: any) {
//     console.error('Failed to create comp:', error)
//     message.error(error?.message || 'Failed to create comp')
//   }
// }

// Delete comp
const handleDeleteComp = async (comp: any) => {
  try {
    await apiClient.deleteComp(comp.id)
    message.success('Comp deleted')
    await loadComps()
    // If we deleted the current comp, switch to list view
    if (currentComp.value?.id === comp.id) {
      viewMode.value = 'list'
      currentComp.value = null
    }
  } catch (error: any) {
    console.error('Failed to delete comp:', error)
    message.error(error?.message || 'Failed to delete comp')
  }
}

// Update comp name
const handleCompNameUpdate = async (comp: any) => {
  editingCompId.value = null // Exit edit mode
  
  if (!comp.name || comp.name.trim() === '') {
    message.warning('Comp name cannot be empty')
    // Restore original name
    await loadComps()
    return
  }
  
  try {
    await apiClient.updateComp(comp.id, { name: comp.name.trim() })
    message.success('Comp name updated')
  } catch (error: any) {
    console.error('Failed to update comp name:', error)
    message.error(error?.message || 'Failed to update comp name')
    // Restore original name
    await loadComps()
  }
}

// Export comp structure as JSON
const handleExportComp = () => {
  if (!currentComp.value) {
    message.warning('No comp selected')
    return
  }
  
  try {
    // Build export structure with all layer data
    const exportData: any = {
      name: currentComp.value.name,
      description: currentComp.value.description || '',
      width: currentComp.value.width,
      height: currentComp.value.height,
      backgroundColor: currentComp.value.backgroundColor || null,
      layers: layers.value.map(layer => {
        const layerData: any = {
          label: layer.name,
          key: layer.name.toLowerCase(),
          type: layer.type === 'image' ? (layer.name === 'BG' ? 'bg' : layer.name === 'LOGO' ? 'logo' : layer.name === 'PHOTO' ? 'photo' : 'sticker') : 'text',
          zIndex: layer.zIndex || 0,
          x: layer.x || 0,
          y: layer.y || 0,
          visible: layer.visible !== false,
          opacity: layer.opacity !== undefined ? layer.opacity : 1.0,
          rotation: ('rotation' in layer ? (layer as any).rotation : 0.0) || 0.0,
          transferMode: layer.blendMode || 'normal',
        }
        
        // Add width/height if present
        if (layer.width !== null && layer.width !== undefined) {
          layerData.width = layer.width
        }
        if (layer.height !== null && layer.height !== undefined) {
          layerData.height = layer.height
        }
        
        // Add type-specific properties
        if (layer.type === 'text') {
          layerData.properties = {
            type: 'text',
            text: layer.copy || '',
            copy: layer.copy || '',
            fontFamily: layer.fontFamily || 'Arial',
            fontSize: layer.fontSize || 24,
            fontWeight: layer.fontWeight || '400',
            color: layer.color || '#000000',
            textAlign: layer.textAlign || 'left',
            lineHeight: 1.2,
            letterSpacing: 0,
          }
        } else if (layer.type === 'image') {
          // For image layers, include asset URL and scale
          const assetUrl = layer.assetUrl || null
          const scale = layer.width && layer.height ? 1.0 : 1.0 // Default scale, could be calculated
          
          layerData.properties = {
            type: layerData.type,
            imageUrl: assetUrl || '',
            imageId: layer.assetId || null,
            scale: scale,
            maintainAspectRatio: true,
          }
          
          // Add specific properties based on layer type
          if (layerData.type === 'photo') {
            layerData.properties.fit = 'cover'
            layerData.properties.position = 'center'
          } else if (layerData.type === 'sticker') {
            layerData.properties.tintColor = undefined // Can be set if needed
          }
        }
        
        return layerData
      })
    }
    
    // Convert to JSON string
    const jsonString = JSON.stringify(exportData, null, 2)
    
    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${currentComp.value.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_comp.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    message.success('Comp exported successfully')
  } catch (error: any) {
    console.error('Failed to export comp:', error)
    message.error(error?.message || 'Failed to export comp')
  }
}

// Select comp
const handleSelectComp = async (comp: any) => {
  try {
    // Ensure assets are loaded first
    if (!assets.value.length && selectedProjectId.value) {
      await loadAssets()
    }
    
    const response = await apiClient.getComp(comp.id)
    currentComp.value = response.comp
    
    // Load layers from comp
    if (response.comp.layers_rel && response.comp.layers_rel.length > 0) {
      // Map comp layers to our layer format
      layers.value = response.comp.layers_rel.map((layer: any) => {
        const props = layer.properties ? JSON.parse(layer.properties) : {}
        if (layer.type === 'text') {
          const textLayer = {
            id: layer.id, // Use database ID
            name: layer.label,
            type: 'text' as const,
            visible: layer.visible,
            opacity: layer.opacity,
            blendMode: layer.transferMode || 'normal',
            copy: props.text || props.copy || '',
            fontFamily: props.fontFamily || 'Arial',
            fontSize: props.fontSize || 24,
            fontWeight: props.fontWeight || '400',
            color: props.color || '#000000',
            textAlign: props.textAlign || 'left',
            x: layer.x,
            y: layer.y,
            zIndex: layer.zIndex || 0
          }
          
          // Try to find matching text style asset for this style
          if (props.styleAssetId || props.assetId || props.imageId) {
            const assetId = props.styleAssetId || props.assetId || props.imageId
            const textAsset = textStyleAssets.value.find(a => a.id === assetId)
            if (textAsset) {
              selectedTextStyleId.value = assetId
            }
          }
          
          // Restore copy asset state
          if (props.copyAssetId) {
            selectedTextCopyId.value = props.copyAssetId
            selectedTextCopyIndex.value = props.copyItemIndex !== undefined ? props.copyItemIndex : 0
          }
          
          return textLayer
        } else {
          const imageLayer = {
            id: layer.id, // Use database ID
            name: layer.label,
            type: 'image' as const,
            visible: layer.visible,
            opacity: layer.opacity,
            blendMode: layer.transferMode || 'normal',
            assetId: props.imageId || props.assetId || layer.srcImage || null,
            assetUrl: props.imageUrl || null,
            assetName: null,
            assetImage: null,
            x: layer.x,
            y: layer.y,
            width: layer.width || null,
            height: layer.height || null,
            rotation: layer.rotation || 0,
            zIndex: layer.zIndex || 0
          }
          
          // Load asset URL if assetId exists
          if (imageLayer.assetId) {
            const asset = assets.value.find(a => a.id === imageLayer.assetId)
            if (asset) {
              try {
                if (asset.metadata) {
                  const meta = JSON.parse(asset.metadata)
                  imageLayer.assetUrl = meta.url || meta.previewUrl || meta.iconUrl || null
                  if (imageLayer.assetUrl) {
                    loadAssetImage(imageLayer)
                  }
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          } else if (imageLayer.assetUrl) {
            loadAssetImage(imageLayer)
          }
          
          return imageLayer
        }
      })
    }
    
    viewMode.value = 'edit'
  } catch (error: any) {
    console.error('Failed to load comp:', error)
    message.error(error?.message || 'Failed to load comp')
  }
}

// Asset selection (direct - no modal)
const handleSelectAssetDirect = (asset: any) => {
  if (selectedImageLayer.value) {
    selectedImageLayer.value.assetId = asset.id
    selectedImageLayer.value.assetName = asset.name
    selectedImageLayer.value.assetImage = null // Reset image
    selectedImageLayer.value.assetUrl = null // Reset URL first
    
    // Use getAssetUrl with preferOriginal=true to get the original PNG (with alpha)
    // instead of the JPG preview thumbnail (without alpha)
    // This is critical for logo layers that need transparency
    const assetUrl = getAssetUrl(asset, true) // preferOriginal=true for compositing
    if (assetUrl) {
      selectedImageLayer.value.assetUrl = assetUrl
      console.log('Selected asset URL (original for alpha):', assetUrl, 'for layer:', selectedImageLayer.value.name)
    } else {
      selectedImageLayer.value.assetUrl = null
      console.warn('No asset URL found for asset:', asset)
    }
    
    // Load the image if we have a URL
    if (selectedImageLayer.value.assetUrl) {
      loadAssetImage(selectedImageLayer.value)
    } else {
      console.warn('No asset URL found for asset:', asset)
    }
  }
}

// Asset selection (for modal - kept for other layer types if needed)
const handleSelectAsset = (asset: any) => {
  handleSelectAssetDirect(asset)
}

// Asset selection from AssetsGrid component
const handleAssetsGridSelect = (asset: any) => {
  // When in list view, just show info or allow drag-to-canvas later
  // When in edit mode with a selected layer, apply the asset
  if (viewMode.value === 'edit' && selectedImageLayer.value) {
    handleSelectAssetDirect(asset)
    message.success(`Selected ${asset.name} for ${selectedImageLayer.value.name} layer`)
  } else {
    // Just show info for now - can be enhanced later for drag-and-drop
    console.log('Asset selected:', asset.name)
  }
}

const confirmAssetSelection = () => {
  showAssetSelector.value = false
  if (selectedImageLayer.value?.assetId) {
    message.success('Asset selected')
    // Re-render canvas with new asset
    nextTick(() => {
      renderCanvas()
    })
  }
}

// Randomize layer asset
const canRandomizeLayer = (layerId: string): boolean => {
  if (!currentComp.value || !selectedProjectId.value) {
    console.debug('canRandomizeLayer: No comp or project selected')
    return false
  }
  const layer = layers.value.find(l => l.id === layerId)
  if (!layer) {
    console.debug('canRandomizeLayer: Layer not found', layerId)
    return false
  }
  
  // Check if layer has a database ID (not a frontend ID like 'layer-1')
  // Frontend IDs start with 'layer-', database IDs are cuid strings
  if (layerId.startsWith('layer-')) {
    console.debug('canRandomizeLayer: Layer has frontend ID, cannot randomize', layerId)
    return false // Can't randomize frontend-only layers
  }
  
  // Ensure assets are loaded
  if (assets.value.length === 0) {
    console.debug('canRandomizeLayer: No assets loaded')
    return false
  }
  
  // Check if there are available assets for this layer type
  if (layer.type === 'image') {
    const layerName = layer.name.toLowerCase()
    if (layerName === 'bg') {
      const canRandomize = bgAssets.value.length > 0
      console.debug('canRandomizeLayer: BG layer', { canRandomize, assetCount: bgAssets.value.length })
      return canRandomize
    }
    if (layerName === 'logo') {
      const canRandomize = logoAssets.value.length > 0
      console.debug('canRandomizeLayer: LOGO layer', { canRandomize, assetCount: logoAssets.value.length })
      return canRandomize
    }
    if (layerName === 'photo') {
      const photoAssets = assets.value.filter(a => a.type === 'photo')
      return photoAssets.length > 0
    }
    if (layerName === 'sticker') {
      const stickerAssets = assets.value.filter(a => a.type === 'sticker')
      return stickerAssets.length > 0
    }
    if (layerName === 'frame') {
      const frameAssets = assets.value.filter(a => a.type === 'frame')
      return frameAssets.length > 0
    }
    // For other image types, check if any assets exist
    const matchingAssets = assets.value.filter(a => a.type === layerName)
    return matchingAssets.length > 0
  } else if (layer.type === 'text') {
    // Check if text assets exist
    const canRandomize = textAssets.value.length > 0
    console.debug('canRandomizeLayer: TEXT layer', { canRandomize, assetCount: textAssets.value.length })
    return canRandomize
  }
  // Unknown layer type
  console.debug('canRandomizeLayer: Unknown layer type', (layer as any).type)
  return false
}

const handleRandomizeLayer = async (layerId: string) => {
  if (!currentComp.value) {
    message.warning('No comp selected')
    return
  }
  
  try {
    const response = await apiClient.randomizeLayer(currentComp.value.id, layerId)
    const updatedLayer = response.layer
    
    // Update local layer state
    const layer = layers.value.find(l => l.id === layerId)
    if (layer) {
      const properties = JSON.parse(updatedLayer.properties)
      
      if (layer.type === 'image') {
        const imageLayer = layer as ImageLayer
        imageLayer.assetId = properties.imageId || properties.assetId || null
        
        // Process the URL - if it's from metadata, use it as-is; if it's a filePath, ensure proper format
        let assetUrl = properties.imageUrl || null
        if (assetUrl) {
          // If URL is relative and doesn't start with /, add /assets/ prefix
          if (assetUrl.startsWith('/assets/')) {
            // Already correct format
          } else if (assetUrl.startsWith('assets/')) {
            assetUrl = `/${assetUrl}`
          } else if (assetUrl.startsWith('/')) {
            // Already absolute, use as-is
          } else if (!assetUrl.startsWith('http')) {
            // Relative path without leading slash, add /assets/
            assetUrl = `/assets/${assetUrl}`
          }
          // If it's a full URL (http/https), use as-is
        }
        
        imageLayer.assetUrl = assetUrl
        imageLayer.assetName = null // Will be updated when asset loads
        imageLayer.assetImage = null
        
        if (imageLayer.assetUrl) {
          loadAssetImage(imageLayer)
        }
      } else if (layer.type === 'text') {
        const textLayer = layer as TextLayer
        // Update copy from properties (prioritize text/copy from backend)
        if (properties.text) textLayer.copy = properties.text
        else if (properties.copy) textLayer.copy = properties.copy
        
        if (properties.fontFamily) textLayer.fontFamily = properties.fontFamily
        if (properties.fontSize) textLayer.fontSize = properties.fontSize
        if (properties.fontWeight) textLayer.fontWeight = properties.fontWeight
        if (properties.color) textLayer.color = properties.color
        if (properties.textAlign) textLayer.textAlign = properties.textAlign
        
        // Update selected text style if styleAssetId or assetId is present
        if (properties.styleAssetId || properties.assetId || properties.imageId) {
          const assetId = properties.styleAssetId || properties.assetId || properties.imageId
          const textAsset = textStyleAssets.value.find(a => a.id === assetId)
          if (textAsset) {
            selectedTextStyleId.value = assetId
          }
        }
        
        // Restore copy asset state
        if (properties.copyAssetId) {
          selectedTextCopyId.value = properties.copyAssetId
          selectedTextCopyIndex.value = properties.copyItemIndex !== undefined ? properties.copyItemIndex : 0
        }
      }
    }
    
    message.success('Layer randomized')
    
    // Re-render canvas
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to randomize layer:', error)
    message.error(error?.message || 'Failed to randomize layer')
  }
}

const getAssetUrl = (asset: any, preferOriginal: boolean = false): string | undefined => {
  try {
    if (asset.metadata) {
      const meta = typeof asset.metadata === 'string' ? JSON.parse(asset.metadata) : asset.metadata
      
      let url: string | undefined
      if (preferOriginal) {
        // For compositing (especially logos with alpha), prefer original URL
        // Original URL preserves format (PNG with alpha) vs previewUrl (JPG without alpha)
        url = meta.url || meta.previewUrl || meta.iconUrl
      } else {
        // For thumbnails/previews, prefer previewUrl (smaller, faster to load)
        url = meta.previewUrl || meta.url || meta.iconUrl
      }
      
      if (url) {
        // If URL is relative, make it absolute
        if (url.startsWith('/')) {
          return `${window.location.origin}${url}`
        }
        return url
      }
    }
    // Fallback to filePath if metadata doesn't exist or doesn't have URL
    if (asset.filePath) {
      // Assets are served at /assets/* (root level, not under /api)
      return `${window.location.origin}/assets/${asset.filePath}`
    }
  } catch (e) {
    console.error('Error parsing asset metadata in getAssetUrl:', e)
  }
  return undefined
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSIjZjVmNWY1IiBzdHJva2U9IiNkMWQ1ZGIiLz4KPHRleHQgeD0iNjAiIHk9IjYwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiPkltYWdlPC90ZXh0Pgo8L3N2Zz4K'
}

// Render canvas with all layers
const renderCanvas = () => {
  if (!canvasRef.value) return
  
  // Get 2D context with alpha channel support (default, but explicit)
  const ctx = canvasRef.value.getContext('2d', { 
    alpha: true,
    willReadFrequently: false 
  })
  if (!ctx) return
  
  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  
  const width = canvasInternalWidth.value
  const height = canvasInternalHeight.value
  
  // Clear entire canvas including alpha channel
  ctx.clearRect(0, 0, width, height)
  
  // Fill with background color (this will have full opacity, which is fine for BG)
  ctx.fillStyle = backgroundColor.value
  ctx.fillRect(0, 0, width, height)
  
  // Sort layers by zIndex
  const sortedLayers = [...layers.value].sort((a, b) => a.zIndex - b.zIndex)
  
  // Render each visible layer
  sortedLayers.forEach(layer => {
    if (!layer.visible) return
    
    ctx.save()
    ctx.globalAlpha = layer.opacity
    
    // Handle blend modes if specified (for now, default to source-over to preserve alpha)
    // source-over is the default and preserves alpha channel correctly
    if (layer.blendMode && layer.blendMode !== 'normal') {
      // Map common blend modes to canvas composite operations
      const blendModeMap: Record<string, GlobalCompositeOperation> = {
        'multiply': 'multiply',
        'screen': 'screen',
        'overlay': 'overlay',
        'darken': 'darken',
        'lighten': 'lighten',
        'color-dodge': 'color-dodge',
        'color-burn': 'color-burn',
        'hard-light': 'hard-light',
        'soft-light': 'soft-light',
        'difference': 'difference',
        'exclusion': 'exclusion'
      }
      ctx.globalCompositeOperation = blendModeMap[layer.blendMode] || 'source-over'
    } else {
      // Default: source-over preserves alpha channel
      ctx.globalCompositeOperation = 'source-over'
    }
    
    if (layer.type === 'image') {
      const imageLayer = layer as ImageLayer
      // For BG layer, check if we have assetUrl (image might still be loading)
      if (imageLayer.name === 'BG' && imageLayer.assetUrl) {
        if (imageLayer.assetImage) {
          const img = imageLayer.assetImage
          console.log('Rendering BG layer, image dimensions:', img.width, 'x', img.height, 'canvas:', width, 'x', height)
          // Use cover behavior - scale to cover entire canvas while maintaining aspect ratio
          const imgAspect = img.width / img.height
          const canvasAspect = width / height
          let drawWidth = width
          let drawHeight = height
          let drawX = 0
          let drawY = 0
          
          if (imgAspect > canvasAspect) {
            // Image is wider - fit to height
            drawHeight = height
            drawWidth = height * imgAspect
            drawX = (width - drawWidth) / 2
          } else {
            // Image is taller - fit to width
            drawWidth = width
            drawHeight = width / imgAspect
            drawY = (height - drawHeight) / 2
          }
          
          console.log('Drawing BG at:', drawX, drawY, 'size:', drawWidth, 'x', drawHeight)
          ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
        } else {
          console.log('BG layer has assetUrl but no assetImage yet:', imageLayer.assetUrl)
        }
      } else if (imageLayer.name !== 'BG') {
        // For other image layers (LOGO, PHOTO, STICKER, FRAME) - preserve alpha channels
        const x = imageLayer.x
        const y = imageLayer.y
        const imgWidth = imageLayer.width || (imageLayer.assetImage ? imageLayer.assetImage.width : 200)
        const imgHeight = imageLayer.height || (imageLayer.assetImage ? imageLayer.assetImage.height : 200)
        
        if (imageLayer.assetImage) {
          const img = imageLayer.assetImage
          const rotation = imageLayer.rotation || 0
          
          // CRITICAL: Set composite operation to preserve alpha channel
          // source-over preserves the alpha channel from the source image
          ctx.globalCompositeOperation = 'source-over'
          
          // For other image layers, apply transforms
          if (rotation !== 0) {
            // Move to center of image, rotate, then move back
            const centerX = x + imgWidth / 2
            const centerY = y + imgHeight / 2
            ctx.translate(centerX, centerY)
            ctx.rotate((rotation * Math.PI) / 180)
            ctx.translate(-centerX, -centerY)
          }
          
          // Draw image - drawImage() preserves alpha channel
          // PNG images with alpha channels will render with transparency
          // Transparent pixels will show through to layers below
          // Use the 9-parameter version to ensure proper rendering
          ctx.drawImage(img, 0, 0, img.width, img.height, x, y, imgWidth, imgHeight)
        } else if (imageLayer.width && imageLayer.height) {
          // Draw placeholder rectangle for layers without images yet
          ctx.fillStyle = 'rgba(200, 200, 200, 0.3)'
          ctx.strokeStyle = 'rgba(150, 150, 150, 0.5)'
          ctx.lineWidth = 2
          ctx.setLineDash([5, 5])
          ctx.fillRect(x, y, imgWidth, imgHeight)
          ctx.strokeRect(x, y, imgWidth, imgHeight)
        }
        
        // Draw selection rectangle and handles for image layers when selected
        if (selectedImageLayer.value?.id === imageLayer.id) {
          ctx.save()
          ctx.strokeStyle = '#18a058' // Primary green color
          ctx.lineWidth = 2
          ctx.setLineDash([])
          ctx.strokeRect(x, y, imgWidth, imgHeight)
          
          // Draw corner handles
          const handleSize = 8
          const handles = [
            { x: x, y: y }, // NW
            { x: x + imgWidth, y: y }, // NE
            { x: x, y: y + imgHeight }, // SW
            { x: x + imgWidth, y: y + imgHeight } // SE
          ]
          
          ctx.fillStyle = '#18a058'
          ctx.strokeStyle = '#ffffff'
          handles.forEach(handle => {
            ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize)
            ctx.strokeRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize)
          })
          
          ctx.restore()
        }
      }
    } else if (layer.type === 'text') {
      const textLayer = layer as TextLayer
      if (textLayer.copy) {
        ctx.font = `${textLayer.fontWeight} ${textLayer.fontSize}px ${textLayer.fontFamily}`
        ctx.fillStyle = textLayer.color
        ctx.textAlign = textLayer.textAlign as CanvasTextAlign
        ctx.textBaseline = 'top'
        
        const x = textLayer.x
        const y = textLayer.y
        
        // Split text into lines and render
        const lines = textLayer.copy.split('\n')
        const lineHeight = textLayer.fontSize * 1.2
        lines.forEach((line, index) => {
          if (line.trim()) {
            ctx.fillText(line, x, y + index * lineHeight)
          }
        })
        
        // Draw selection rectangle and handles for text layers when selected
        if (selectedTextLayer.value?.id === textLayer.id) {
          // Always recalculate bounds from actual text (don't use stored values for rendering)
          const bounds = getTextLayerBounds(textLayer, true)
          ctx.save()
          ctx.strokeStyle = '#18a058' // Primary green color
          ctx.lineWidth = 2
          ctx.setLineDash([])
          
          // Calculate rectangle position based on text alignment
          let rectX = x
          const textAlign = textLayer.textAlign || 'left'
          if (textAlign === 'center') {
            rectX = x - bounds.width / 2
          } else if (textAlign === 'right') {
            rectX = x - bounds.width
          }
          
          // Draw rectangle at exact text position (accounting for alignment)
          ctx.strokeRect(rectX, y, bounds.width, bounds.height)
          
          // Draw corner handles (using rectX for alignment)
          const handleSize = 8
          const handles = [
            { x: rectX, y: y }, // NW
            { x: rectX + bounds.width, y: y }, // NE
            { x: rectX, y: y + bounds.height }, // SW
            { x: rectX + bounds.width, y: y + bounds.height } // SE
          ]
          
          ctx.fillStyle = '#18a058'
          ctx.strokeStyle = '#ffffff'
          handles.forEach(handle => {
            ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize)
            ctx.strokeRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize)
          })
          
          ctx.restore()
        }
      }
    }
    
    ctx.restore()
  })
}

// Load asset image
const loadAssetImage = (layer: ImageLayer) => {
  if (!layer.assetUrl) {
    console.warn('No assetUrl provided for layer:', layer.name)
    return
  }
  
  if (layer.assetImage) {
    console.log('Image already loaded for layer:', layer.name)
    return
  }
  
  console.log('Loading image for layer:', layer.name, 'URL:', layer.assetUrl)
  
  // Try loading with crossOrigin first (for CORS-enabled servers)
  // If that fails due to CORS, fall back to loading without crossOrigin
  // Alpha channels will still be preserved even without crossOrigin when drawing to canvas
  const tryLoadImage = (withCORS: boolean) => {
    const img = new Image()
    
    if (withCORS) {
      // Try with CORS first - allows reading pixel data if CORS headers are present
      img.crossOrigin = 'anonymous'
    } else {
      // Without CORS - image will still load and alpha will be preserved when drawing
      // We just can't read pixel data (which we don't need for rendering)
    }
    
    img.onload = () => {
      console.log(`Image loaded successfully for layer: ${layer.name} (${withCORS ? 'with CORS' : 'without CORS'}), Dimensions: ${img.width}x${img.height}`)
      layer.assetImage = img
      
      // For LOGO and PHOTO layers, update size to match image aspect ratio
      if (layer.name === 'LOGO' || layer.name === 'PHOTO') {
        if (!layer.width || !layer.height) {
          // No size set - use image dimensions
          layer.width = img.width
          layer.height = img.height
          // Center the layer on canvas initially
          layer.x = (canvasInternalWidth.value - img.width) / 2
          layer.y = (canvasInternalHeight.value - img.height) / 2
        } else {
          // Size is set - adjust to maintain image aspect ratio
          const currentAspect = layer.width / layer.height
          const imageAspect = img.width / img.height
          
          // If aspect ratios differ significantly, adjust to match image aspect ratio
          if (Math.abs(currentAspect - imageAspect) > 0.01) {
            // Maintain the current width, adjust height to match image aspect ratio
            layer.height = layer.width / imageAspect
          }
        }
      }
      
      nextTick(() => {
        renderCanvas()
      })
    }
    
    img.onerror = (error) => {
      if (withCORS) {
        // CORS failed, silently retry without CORS (expected for some CDNs like DigitalOcean Spaces)
        tryLoadImage(false)
      } else {
        // Both attempts failed
        console.error('Failed to load asset image for layer:', layer.name, 'URL:', layer.assetUrl, 'Error:', error)
      }
    }
    
    if (layer.assetUrl) {
      img.src = layer.assetUrl
    }
  }
  
  // Start by trying with CORS
  tryLoadImage(true)
}

const updateContainerSize = () => {
  if (canvasContainerRef.value) {
    const rect = canvasContainerRef.value.getBoundingClientRect()
    containerSize.value = {
      width: rect.width - 48,
      height: rect.height - 48
    }
  }
}

const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  message.info('Drop functionality coming soon')
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
}

// Convert screen coordinates to canvas coordinates
const screenToCanvas = (screenX: number, screenY: number) => {
  if (!canvasRef.value) return { x: 0, y: 0 }
  const rect = canvasRef.value.getBoundingClientRect()
  const scaleX = canvasInternalWidth.value / rect.width
  const scaleY = canvasInternalHeight.value / rect.height
  return {
    x: (screenX - rect.left) * scaleX,
    y: (screenY - rect.top) * scaleY
  }
}

// Check if point is inside a handle
const getHandleAtPoint = (x: number, y: number, layer: ImageLayer): 'nw' | 'ne' | 'sw' | 'se' | null => {
  const imgWidth = layer.width || (layer.assetImage ? layer.assetImage.width : 200)
  const imgHeight = layer.height || (layer.assetImage ? layer.assetImage.height : 200)
  const handleSize = 8
  const threshold = handleSize * 2 // Make handles easier to grab
  
  const handles = [
    { type: 'nw' as const, x: layer.x, y: layer.y },
    { type: 'ne' as const, x: layer.x + imgWidth, y: layer.y },
    { type: 'sw' as const, x: layer.x, y: layer.y + imgHeight },
    { type: 'se' as const, x: layer.x + imgWidth, y: layer.y + imgHeight }
  ]
  
  for (const handle of handles) {
    const dx = x - handle.x
    const dy = y - handle.y
    const distance = Math.sqrt(dx * dx + dy * dy)
    if (distance <= threshold) {
      return handle.type
    }
  }
  
  return null
}

// Check if point is inside the logo bounds
// Check if point is inside the image layer bounds (LOGO, PHOTO, etc.)
const isPointInImageLayer = (x: number, y: number, layer: ImageLayer): boolean => {
  const imgWidth = layer.width || (layer.assetImage ? layer.assetImage.width : 200)
  const imgHeight = layer.height || (layer.assetImage ? layer.assetImage.height : 200)
  return x >= layer.x && x <= layer.x + imgWidth && y >= layer.y && y <= layer.y + imgHeight
}

// Calculate text layer bounds
const getTextLayerBounds = (layer: TextLayer, forceRecalculate = false): { width: number; height: number } => {
  // If we have stored bounds and not forcing recalculation, use them
  if (!forceRecalculate && layer.width && layer.height) {
    return { width: layer.width, height: layer.height }
  }
  
  // Calculate bounds from text content
  if (!canvasRef.value) return { width: 200, height: 50 }
  
  const ctx = canvasRef.value.getContext('2d')
  if (!ctx) return { width: 200, height: 50 }
  
  // Set font to match rendering
  ctx.font = `${layer.fontWeight} ${layer.fontSize}px ${layer.fontFamily}`
  
  const lines = layer.copy.split('\n').filter(line => line.trim())
  const lineHeight = layer.fontSize * 1.2
  
  let maxWidth = 0
  lines.forEach(line => {
    const metrics = ctx.measureText(line)
    maxWidth = Math.max(maxWidth, metrics.width)
  })
  
  // Return actual text bounds (no padding needed - measureText gives accurate width)
  return {
    width: maxWidth || 200,
    height: lines.length > 0 ? lines.length * lineHeight : layer.fontSize * 1.2
  }
}

// Get actual bounding box rectangle accounting for text alignment
// layer.x is the anchor point (varies by alignment), but we need the actual bounding box
const getTextBoundingBox = (layer: TextLayer, forceRecalculate = false): { x: number; y: number; width: number; height: number } => {
  const bounds = getTextLayerBounds(layer, forceRecalculate)
  const textAlign = layer.textAlign || 'left'
  
  let x: number
  if (textAlign === 'center') {
    x = layer.x - bounds.width / 2
  } else if (textAlign === 'right') {
    x = layer.x - bounds.width
  } else {
    // left alignment
    x = layer.x
  }
  
  return {
    x,
    y: layer.y,
    width: bounds.width,
    height: bounds.height
  }
}

// Convert bounding box position to anchor point based on text alignment
const boundingBoxToAnchor = (bboxX: number, bboxWidth: number, textAlign: 'left' | 'center' | 'right'): number => {
  if (textAlign === 'center') {
    return bboxX + bboxWidth / 2
  } else if (textAlign === 'right') {
    return bboxX + bboxWidth
  } else {
    // left alignment
    return bboxX
  }
}

// Check if point is inside text layer bounds
const isPointInTextLayer = (x: number, y: number, layer: TextLayer): boolean => {
  const bbox = getTextBoundingBox(layer)
  return x >= bbox.x && x <= bbox.x + bbox.width && y >= bbox.y && y <= bbox.y + bbox.height
}

// Get handle at point for text layer
const getHandleAtPointForText = (x: number, y: number, layer: TextLayer): 'nw' | 'ne' | 'sw' | 'se' | null => {
  const bbox = getTextBoundingBox(layer)
  const handleSize = 8
  const threshold = handleSize * 2
  
  const handles = [
    { type: 'nw' as const, x: bbox.x, y: bbox.y },
    { type: 'ne' as const, x: bbox.x + bbox.width, y: bbox.y },
    { type: 'sw' as const, x: bbox.x, y: bbox.y + bbox.height },
    { type: 'se' as const, x: bbox.x + bbox.width, y: bbox.y + bbox.height }
  ]
  
  for (const handle of handles) {
    const dx = x - handle.x
    const dy = y - handle.y
    const distance = Math.sqrt(dx * dx + dy * dy)
    if (distance <= threshold) {
      return handle.type
    }
  }
  
  return null
}

const handleCanvasMouseDown = (event: MouseEvent) => {
  if (!canvasRef.value) return
  
  const canvasPos = screenToCanvas(event.clientX, event.clientY)
  
  // Handle image layers (LOGO, PHOTO)
  if (selectedImageLayer.value && (selectedImageLayer.value.name === 'LOGO' || selectedImageLayer.value.name === 'PHOTO')) {
    const layer = selectedImageLayer.value
    
    // Check if clicking on a resize handle
    const handle = getHandleAtPoint(canvasPos.x, canvasPos.y, layer)
    if (handle) {
      isDragging.value = true
      dragType.value = 'resize'
      resizeHandle.value = handle
      dragStartPos.value = canvasPos
      dragStartLayerState.value = {
        x: layer.x,
        y: layer.y,
        width: layer.width || (layer.assetImage ? layer.assetImage.width : 200),
        height: layer.height || (layer.assetImage ? layer.assetImage.height : 200),
        fontSize: 0
      }
      event.preventDefault()
      return
    }
    
    // Check if clicking on the image layer itself
    if (isPointInImageLayer(canvasPos.x, canvasPos.y, layer)) {
      isDragging.value = true
      dragType.value = 'move'
      dragStartPos.value = canvasPos
      dragStartLayerState.value = {
        x: layer.x,
        y: layer.y,
        width: layer.width || (layer.assetImage ? layer.assetImage.width : 200),
        height: layer.height || (layer.assetImage ? layer.assetImage.height : 200),
        fontSize: 0
      }
      event.preventDefault()
    }
    return
  }
  
  // Handle text layers
  if (selectedTextLayer.value) {
    const layer = selectedTextLayer.value
    
    // Check if clicking on a resize handle
    const handle = getHandleAtPointForText(canvasPos.x, canvasPos.y, layer)
    if (handle) {
      isDragging.value = true
      dragType.value = 'resize'
      resizeHandle.value = handle
      dragStartPos.value = canvasPos
      // Store actual bounding box position (not anchor point) for resize calculations
      // Also store initial fontSize to calculate scale relative to initial state (prevents exponential scaling)
      const bbox = getTextBoundingBox(layer)
      dragStartLayerState.value = {
        x: bbox.x,
        y: bbox.y,
        width: bbox.width,
        height: bbox.height,
        fontSize: layer.fontSize
      }
      event.preventDefault()
      return
    }
    
    // Check if clicking on the text layer itself
    if (isPointInTextLayer(canvasPos.x, canvasPos.y, layer)) {
      isDragging.value = true
      dragType.value = 'move'
      dragStartPos.value = canvasPos
      // Store actual bounding box position for movement calculations
      const bbox = getTextBoundingBox(layer)
      dragStartLayerState.value = {
        x: bbox.x,
        y: bbox.y,
        width: bbox.width,
        height: bbox.height,
        fontSize: 0 // Not needed for movement, but keep structure consistent
      }
      event.preventDefault()
    }
    return
  }
}

const handleCanvasMouseMove = (event: MouseEvent) => {
  if (!canvasRef.value) return
  
  const canvasPos = screenToCanvas(event.clientX, event.clientY)
  
  // Handle image layers (LOGO, PHOTO)
  if (selectedImageLayer.value && (selectedImageLayer.value.name === 'LOGO' || selectedImageLayer.value.name === 'PHOTO')) {
    const layer = selectedImageLayer.value
    
    // Update cursor based on what's under the mouse
    if (!isDragging.value) {
      const handle = getHandleAtPoint(canvasPos.x, canvasPos.y, layer)
      if (handle) {
        // Set cursor based on handle direction
        const cursors: Record<string, string> = {
          'nw': 'nw-resize',
          'ne': 'ne-resize',
          'sw': 'sw-resize',
          'se': 'se-resize'
        }
        if (canvasRef.value) {
          canvasRef.value.style.cursor = cursors[handle] || 'default'
        }
      } else if (isPointInImageLayer(canvasPos.x, canvasPos.y, layer)) {
        if (canvasRef.value) {
          canvasRef.value.style.cursor = 'move'
        }
      } else {
        if (canvasRef.value) {
          canvasRef.value.style.cursor = 'default'
        }
      }
    }
    
    // Handle dragging
    if (isDragging.value) {
      const deltaX = canvasPos.x - dragStartPos.value.x
      const deltaY = canvasPos.y - dragStartPos.value.y
      
      if (dragType.value === 'move') {
        // Move the image layer
        layer.x = dragStartLayerState.value.x + deltaX
        layer.y = dragStartLayerState.value.y + deltaY
        
        // Keep within canvas bounds
        const imgWidth = layer.width || (layer.assetImage ? layer.assetImage.width : 200)
        const imgHeight = layer.height || (layer.assetImage ? layer.assetImage.height : 200)
        layer.x = Math.max(0, Math.min(layer.x, canvasInternalWidth.value - imgWidth))
        layer.y = Math.max(0, Math.min(layer.y, canvasInternalHeight.value - imgHeight))
      } else if (dragType.value === 'resize' && resizeHandle.value) {
        // Resize the image layer based on which handle is being dragged
        // Maintain aspect ratio (uniform scaling) - use Math.max to ensure both dimensions scale together
        const startX = dragStartLayerState.value.x
        const startY = dragStartLayerState.value.y
        const startWidth = dragStartLayerState.value.width
        const startHeight = dragStartLayerState.value.height
        
        // Calculate scale based on the primary axis of the handle being dragged
        // Using Math.max ensures uniform scaling (maintains aspect ratio)
        let scale: number
        
        switch (resizeHandle.value) {
          case 'nw':
            // Use the larger delta to maintain aspect ratio
            scale = Math.max(
              (startWidth - deltaX) / startWidth,
              (startHeight - deltaY) / startHeight
            )
            const newWidthNW = startWidth * scale
            const newHeightNW = startHeight * scale
            layer.x = startX + startWidth - newWidthNW
            layer.y = startY + startHeight - newHeightNW
            layer.width = newWidthNW
            layer.height = newHeightNW
            break
          case 'ne':
            scale = Math.max(
              (startWidth + deltaX) / startWidth,
              (startHeight - deltaY) / startHeight
            )
            const newWidthNE = startWidth * scale
            const newHeightNE = startHeight * scale
            layer.x = startX
            layer.y = startY + startHeight - newHeightNE
            layer.width = newWidthNE
            layer.height = newHeightNE
            break
          case 'sw':
            scale = Math.max(
              (startWidth - deltaX) / startWidth,
              (startHeight + deltaY) / startHeight
            )
            const newWidthSW = startWidth * scale
            const newHeightSW = startHeight * scale
            layer.x = startX + startWidth - newWidthSW
            layer.y = startY
            layer.width = newWidthSW
            layer.height = newHeightSW
            break
          case 'se':
            // SE corner - use the larger delta to maintain aspect ratio
            scale = Math.max(
              (startWidth + deltaX) / startWidth,
              (startHeight + deltaY) / startHeight
            )
            const newWidthSE = startWidth * scale
            const newHeightSE = startHeight * scale
            layer.x = startX
            layer.y = startY
            layer.width = newWidthSE
            layer.height = newHeightSE
            break
        }
        
        // Enforce minimum size (maintain aspect ratio)
        const minSize = 10
        if (layer.width! < minSize || layer.height! < minSize) {
          const minScale = Math.max(minSize / layer.width!, minSize / layer.height!)
          layer.width = layer.width! * minScale
          layer.height = layer.height! * minScale
          
          // Adjust position to keep the correct corner fixed
          switch (resizeHandle.value) {
            case 'nw':
              layer.x = startX + startWidth - layer.width
              layer.y = startY + startHeight - layer.height
              break
            case 'ne':
              layer.y = startY + startHeight - layer.height
              break
            case 'sw':
              layer.x = startX + startWidth - layer.width
              break
            // SE doesn't need adjustment
          }
        }
      }
      
      renderCanvas()
    }
    return
  }
  
  // Handle text layers
  if (selectedTextLayer.value) {
    const layer = selectedTextLayer.value
    
    // Update cursor based on what's under the mouse
    if (!isDragging.value) {
      const handle = getHandleAtPointForText(canvasPos.x, canvasPos.y, layer)
      if (handle) {
        // Set cursor based on handle direction
        const cursors: Record<string, string> = {
          'nw': 'nw-resize',
          'ne': 'ne-resize',
          'sw': 'sw-resize',
          'se': 'se-resize'
        }
        if (canvasRef.value) {
          canvasRef.value.style.cursor = cursors[handle] || 'default'
        }
      } else if (isPointInTextLayer(canvasPos.x, canvasPos.y, layer)) {
        if (canvasRef.value) {
          canvasRef.value.style.cursor = 'move'
        }
      } else {
        if (canvasRef.value) {
          canvasRef.value.style.cursor = 'default'
        }
      }
    }
    
    // Handle dragging
    if (isDragging.value) {
      const deltaX = canvasPos.x - dragStartPos.value.x
      const deltaY = canvasPos.y - dragStartPos.value.y
      
      if (dragType.value === 'move') {
        // Move the text layer
        // dragStartLayerState contains bounding box position (not anchor point)
        const newBboxX = dragStartLayerState.value.x + deltaX
        const newBboxY = dragStartLayerState.value.y + deltaY
        const bboxWidth = dragStartLayerState.value.width
        
        // Convert bounding box position back to anchor point based on text alignment
        // No bounds checking - allow positioning anywhere
        const textAlign = layer.textAlign || 'left'
        layer.x = boundingBoxToAnchor(newBboxX, bboxWidth, textAlign)
        layer.y = newBboxY
      } else if (dragType.value === 'resize' && resizeHandle.value) {
        // Resize the text layer - adjust fontSize based on scale
        // dragStartLayerState contains bounding box position (not anchor point)
        const startBboxX = dragStartLayerState.value.x
        const startBboxY = dragStartLayerState.value.y
        const startWidth = dragStartLayerState.value.width
        const startHeight = dragStartLayerState.value.height
        
        // Calculate scale using average of both axes for more stable behavior
        // This prevents erratic jumps that occur with Math.max
        let scaleX: number
        let scaleY: number
        
        switch (resizeHandle.value) {
          case 'nw':
            scaleX = (startWidth - deltaX) / startWidth
            scaleY = (startHeight - deltaY) / startHeight
            break
          case 'ne':
            scaleX = (startWidth + deltaX) / startWidth
            scaleY = (startHeight - deltaY) / startHeight
            break
          case 'sw':
            scaleX = (startWidth - deltaX) / startWidth
            scaleY = (startHeight + deltaY) / startHeight
            break
          case 'se':
            scaleX = (startWidth + deltaX) / startWidth
            scaleY = (startHeight + deltaY) / startHeight
            break
          default:
            scaleX = 1
            scaleY = 1
        }
        
        // Use average of both scales for more predictable, stable scaling
        // This maintains aspect ratio while being less sensitive to small movements
        let scale = (scaleX + scaleY) / 2
        
        // Clamp scale to reasonable bounds per drag operation (0.5x to 2x)
        // This prevents the scale from getting too big or too small too quickly
        scale = Math.max(0.5, Math.min(2, scale))
        
        // Calculate new fontSize relative to INITIAL fontSize (not current)
        // This prevents exponential scaling that occurs when applying scale to already-scaled values
        const initialFontSize = dragStartLayerState.value.fontSize || layer.fontSize
        const scaledFontSize = initialFontSize * scale
        
        // Clamp final fontSize to absolute min/max bounds (8px to 200px)
        const newFontSize = Math.max(8, Math.min(200, scaledFontSize))
        
        // Update fontSize first
        layer.fontSize = newFontSize
        
        // Recalculate actual text bounds based on new fontSize (force recalculation)
        const newBounds = getTextLayerBounds(layer, true)
        const newWidth = newBounds.width
        const newHeight = newBounds.height
        
        // Calculate new bounding box position based on which handle is being dragged
        let newBboxX: number
        let newBboxY: number
        
        switch (resizeHandle.value) {
          case 'nw':
            // NW: adjust both x and y to keep NW corner fixed
            newBboxX = startBboxX + startWidth - newWidth
            newBboxY = startBboxY + startHeight - newHeight
            break
          case 'ne':
            // NE: adjust only y to keep NE corner fixed
            newBboxX = startBboxX
            newBboxY = startBboxY + startHeight - newHeight
            break
          case 'sw':
            // SW: adjust only x to keep SW corner fixed
            newBboxX = startBboxX + startWidth - newWidth
            newBboxY = startBboxY
            break
          case 'se':
            // SE: no position adjustment needed
            newBboxX = startBboxX
            newBboxY = startBboxY
            break
          default:
            newBboxX = startBboxX
            newBboxY = startBboxY
        }
        
        // Enforce minimum size
        const minSize = 20
        if (newWidth < minSize || newHeight < minSize) {
          const minScale = Math.max(minSize / newWidth, minSize / newHeight)
          // Calculate fontSize relative to initial fontSize (not current)
          const initialFontSize = dragStartLayerState.value.fontSize || layer.fontSize
          const minScaledFontSize = initialFontSize * scale * minScale
          layer.fontSize = Math.max(8, Math.min(200, minScaledFontSize))
          const adjustedWidth = newWidth * minScale
          const adjustedHeight = newHeight * minScale
          
          // Adjust bounding box position for minimum size
          switch (resizeHandle.value) {
            case 'nw':
              newBboxX = startBboxX + startWidth - adjustedWidth
              newBboxY = startBboxY + startHeight - adjustedHeight
              break
            case 'ne':
              newBboxY = startBboxY + startHeight - adjustedHeight
              break
            case 'sw':
              newBboxX = startBboxX + startWidth - adjustedWidth
              break
          }
          
          // Store adjusted bounds
          layer.width = adjustedWidth
          layer.height = adjustedHeight
        } else {
          // Store the actual calculated bounds
          layer.width = newWidth
          layer.height = newHeight
        }
        
        // Convert bounding box position back to anchor point based on text alignment
        const textAlign = layer.textAlign || 'left'
        layer.x = boundingBoxToAnchor(newBboxX, layer.width || newWidth, textAlign)
        layer.y = newBboxY
        
        renderCanvas()
      }
    }
    return
  }
  
  // No layer selected - reset cursor
  if (canvasRef.value && !isDragging.value) {
    canvasRef.value.style.cursor = 'default'
  }
}

const handleCanvasMouseUp = () => {
  // If we were resizing a text layer, round the scale factor for display
  if (isDragging.value && dragType.value === 'resize' && selectedTextLayer.value) {
    // Round the fontSize to ensure scale factor displays cleanly
    // This happens automatically via the computed property, but we can force a reactive update
    const currentFontSize = selectedTextLayer.value.fontSize
    // Round to nearest 0.01 to match 2 decimal precision in scale factor
    const roundedFontSize = Math.round(currentFontSize * 100) / 100
    if (Math.abs(currentFontSize - roundedFontSize) > 0.001) {
      selectedTextLayer.value.fontSize = roundedFontSize
      // Update bounds
      const bounds = getTextLayerBounds(selectedTextLayer.value, true)
      selectedTextLayer.value.width = bounds.width
      selectedTextLayer.value.height = bounds.height
      
      // Save rounded value to backend
      if (currentComp.value) {
        apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
          properties: JSON.stringify({
            type: 'text',
            text: selectedTextLayer.value.copy,
            copy: selectedTextLayer.value.copy,
            fontFamily: selectedTextLayer.value.fontFamily,
            fontSize: roundedFontSize,
            fontWeight: selectedTextLayer.value.fontWeight,
            color: selectedTextLayer.value.color,
            textAlign: selectedTextLayer.value.textAlign,
            styleAssetId: selectedTextStyleId.value,
            assetId: selectedTextStyleId.value,
            imageId: selectedTextStyleId.value,
          })
        }).catch((error: any) => {
          console.error('Failed to save rounded fontSize:', error)
        })
      }
      
      nextTick(() => {
        renderCanvas()
      })
    }
  }
  
  isDragging.value = false
  dragType.value = null
  resizeHandle.value = null
}



// Layer methods
const handleLayerChange = (layerId: string) => {
  selectedLayerId.value = layerId
  nextTick(() => {
    renderCanvas()
  })
}

const toggleLayerVisibility = (visible: boolean) => {
  const layer = selectedLayer.value
  if (layer) {
    layer.visible = visible
    message.info(`${layer.name} ${visible ? 'shown' : 'hidden'}`)
    nextTick(() => {
      renderCanvas()
    })
  }
}

// Base fontSize for scale factor calculation (default text size)
const BASE_FONT_SIZE = 24

// Get current style's base fontSize
const getCurrentStyleBaseFontSize = (): number => {
  if (selectedTextStyleId.value) {
    const currentStyleAsset = textStyleAssets.value.find(a => a.id === selectedTextStyleId.value)
    if (currentStyleAsset?.metadata) {
      try {
        const metadata = typeof currentStyleAsset.metadata === 'string' 
          ? JSON.parse(currentStyleAsset.metadata) 
          : currentStyleAsset.metadata
        if (metadata.fontSize) {
          return metadata.fontSize
        }
      } catch (e) {
        // Use default
      }
    }
  }
  return BASE_FONT_SIZE
}

// Computed properties for text layer controls
const textScaleFactor = computed({
  get: () => {
    if (!selectedTextLayer.value) return 1
    // Calculate scale relative to current style's base fontSize
    const baseFontSize = getCurrentStyleBaseFontSize()
    const scale = selectedTextLayer.value.fontSize / baseFontSize
    // Round to 2 decimal places to prevent long decimals from showing
    return Math.round(scale * 100) / 100
  },
  set: (value: number) => {
    if (!selectedTextLayer.value) return
    handleTextScaleChange(value)
  }
})

const textPositionX = computed({
  get: () => {
    if (!selectedTextLayer.value) return 0
    return Math.round(selectedTextLayer.value.x)
  },
  set: (value: number) => {
    if (!selectedTextLayer.value) return
    handleTextPositionXChange(value)
  }
})

const textPositionY = computed({
  get: () => {
    if (!selectedTextLayer.value) return 0
    return Math.round(selectedTextLayer.value.y)
  },
  set: (value: number) => {
    if (!selectedTextLayer.value) return
    handleTextPositionYChange(value)
  }
})

// Handle text scale change
const handleTextScaleChange = async (scale: number | null) => {
  if (!selectedTextLayer.value || !currentComp.value || scale === null) return
  
  // Round scale to 2 decimal places
  const roundedScale = Math.round(scale * 100) / 100
  // Apply scale to current style's base fontSize
  const baseFontSize = getCurrentStyleBaseFontSize()
  const newFontSize = Math.max(8, Math.min(200, baseFontSize * roundedScale))
  selectedTextLayer.value.fontSize = newFontSize
  
  // Update stored bounds
  const bounds = getTextLayerBounds(selectedTextLayer.value, true)
  selectedTextLayer.value.width = bounds.width
  selectedTextLayer.value.height = bounds.height
  
  // Save to backend
  try {
    await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
      properties: JSON.stringify({
        type: 'text',
        text: selectedTextLayer.value.copy,
        copy: selectedTextLayer.value.copy,
        fontFamily: selectedTextLayer.value.fontFamily,
        fontSize: newFontSize,
        fontWeight: selectedTextLayer.value.fontWeight,
        color: selectedTextLayer.value.color,
        textAlign: selectedTextLayer.value.textAlign,
        styleAssetId: selectedTextStyleId.value,
        assetId: selectedTextStyleId.value,
        imageId: selectedTextStyleId.value,
      })
    })
    
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to update text scale:', error)
    message.error(error?.message || 'Failed to update text scale')
  }
}

// Handle scale factor blur - ensure value is properly formatted
const handleScaleFactorBlur = () => {
  // The computed property will automatically round the value
  // This handler just ensures the input doesn't clear on blur
  if (selectedTextLayer.value) {
    // Force a reactive update to ensure the rounded value is displayed
    const currentScale = textScaleFactor.value
    if (currentScale !== undefined && currentScale !== null) {
      // The computed getter will return the rounded value
      // No need to do anything, just let Vue update the display
    }
  }
}

// Handle text position X change
const handleTextPositionXChange = async (x: number | null) => {
  if (!selectedTextLayer.value || !currentComp.value || x === null) return
  
  selectedTextLayer.value.x = x
  
  // Save to backend
  try {
    await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
      x: x
    })
    
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to update text position:', error)
    message.error(error?.message || 'Failed to update text position')
  }
}

// Handle text position Y change
const handleTextPositionYChange = async (y: number | null) => {
  if (!selectedTextLayer.value || !currentComp.value || y === null) return
  
  selectedTextLayer.value.y = y
  
  // Save to backend
  try {
    await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
      y: y
    })
    
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to update text position:', error)
    message.error(error?.message || 'Failed to update text position')
  }
}

// Reset text layer: scale to 1, center on canvas
const handleResetTextLayer = async () => {
  if (!selectedTextLayer.value || !currentComp.value) return
  
  try {
    // Reset fontSize to base
    selectedTextLayer.value.fontSize = BASE_FONT_SIZE
    
    // Recalculate bounds
    const bounds = getTextLayerBounds(selectedTextLayer.value, true)
    selectedTextLayer.value.width = bounds.width
    selectedTextLayer.value.height = bounds.height
    
    // Center on canvas based on text alignment
    const textAlign = selectedTextLayer.value.textAlign || 'left'
    let centerX: number
    if (textAlign === 'center') {
      centerX = canvasInternalWidth.value / 2
    } else if (textAlign === 'right') {
      centerX = canvasInternalWidth.value - bounds.width / 2
    } else {
      // left alignment
      centerX = (canvasInternalWidth.value - bounds.width) / 2
    }
    
    selectedTextLayer.value.x = centerX
    selectedTextLayer.value.y = (canvasInternalHeight.value - bounds.height) / 2
    
    // Save to backend
    await apiClient.updateCompLayer(currentComp.value.id, selectedTextLayer.value.id, {
      x: selectedTextLayer.value.x,
      y: selectedTextLayer.value.y,
      properties: JSON.stringify({
        type: 'text',
        text: selectedTextLayer.value.copy,
        copy: selectedTextLayer.value.copy,
        fontFamily: selectedTextLayer.value.fontFamily,
        fontSize: BASE_FONT_SIZE,
        fontWeight: selectedTextLayer.value.fontWeight,
        color: selectedTextLayer.value.color,
        textAlign: selectedTextLayer.value.textAlign,
        styleAssetId: selectedTextStyleId.value,
        assetId: selectedTextStyleId.value,
        imageId: selectedTextStyleId.value,
      })
    })
    
    message.success('Text layer reset')
    nextTick(() => {
      renderCanvas()
    })
  } catch (error: any) {
    console.error('Failed to reset text layer:', error)
    message.error(error?.message || 'Failed to reset text layer')
  }
}

const formatDate = (date: string | Date): string => {
  if (!date) return 'Unknown'
  try {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString()
  } catch (e) {
    return 'Invalid date'
  }
}

let resizeObserver: ResizeObserver | null = null

// Watch for changes and re-render canvas
watch(aspectRatio, () => {
  nextTick(() => {
    renderCanvas()
  })
})

watch(backgroundColor, () => {
  nextTick(() => {
    renderCanvas()
  })
})

watch(layers, () => {
  nextTick(() => {
    renderCanvas()
  })
}, { deep: true })

// Listen for brand/project changes
onMounted(() => {
  window.addEventListener('brand-project-changed', handleBrandProjectChanged as EventListener)
  
  // Initial load
  const savedBrandId = localStorage.getItem('selectedBrandId')
  if (savedBrandId) {
    const savedProjectId = localStorage.getItem(`selectedProject_${savedBrandId}`)
    if (savedProjectId) {
      selectedProjectId.value = savedProjectId
      // Load project details
      loadProject()
      loadComps()
      loadAssets()
      loadDeliverables()
    }
  }
  
  nextTick(() => {
    if (viewMode.value === 'edit') {
      updateContainerSize()
      renderCanvas()
      
      if (canvasContainerRef.value) {
        resizeObserver = new ResizeObserver(() => {
          updateContainerSize()
        })
        resizeObserver.observe(canvasContainerRef.value)
        window.addEventListener('resize', updateContainerSize)
      }
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('brand-project-changed', handleBrandProjectChanged as EventListener)
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('resize', updateContainerSize)
})

const handleBrandProjectChanged = async (event: Event) => {
  const customEvent = event as CustomEvent<{ brandId?: string | null; projectId?: string | null }>
  const brandId = customEvent?.detail?.brandId || localStorage.getItem('selectedBrandId')
  const projectId = customEvent?.detail?.projectId || (brandId ? localStorage.getItem(`selectedProject_${brandId}`) : null)
  
  if (projectId && projectId !== selectedProjectId.value) {
    selectedProjectId.value = projectId
    // Load project details
    try {
      const projectResponse = await apiClient.getProject(projectId)
      selectedProject.value = projectResponse.project
    } catch (error) {
      console.error('Failed to load project:', error)
    }
    loadComps()
    loadAssets()
  } else if (!projectId) {
    selectedProjectId.value = null
    selectedProject.value = null
    comps.value = []
    assets.value = []
    deliverables.value = []
  }
}

// Watch view mode changes
watch(viewMode, (newMode) => {
  if (newMode === 'edit') {
    nextTick(() => {
      updateContainerSize()
      renderCanvas()
      
      if (canvasContainerRef.value && !resizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          updateContainerSize()
        })
        resizeObserver.observe(canvasContainerRef.value)
        window.addEventListener('resize', updateContainerSize)
      }
    })
  }
})

// Watch for asset URL changes to load images (debounced to prevent loops)
let assetUrlWatchTimeout: ReturnType<typeof setTimeout> | null = null
watch(() => layers.value.map(l => l.type === 'image' ? (l as ImageLayer).assetUrl : null), () => {
  // Clear any pending timeout
  if (assetUrlWatchTimeout) {
    clearTimeout(assetUrlWatchTimeout)
  }
  
  // Debounce the image loading to prevent render loops
  assetUrlWatchTimeout = setTimeout(() => {
    layers.value.forEach(layer => {
      if (layer.type === 'image') {
        const imageLayer = layer as ImageLayer
        if (imageLayer.assetUrl && !imageLayer.assetImage) {
          loadAssetImage(imageLayer)
        }
      }
    })
  }, 100)
}, { deep: true })

// Watch for assetImage changes to re-render when images load
watch(() => layers.value.map(l => l.type === 'image' ? (l as ImageLayer).assetImage : null), () => {
  nextTick(() => {
    renderCanvas()
  })
}, { deep: true })

// Watch selected layers for property changes
watch(selectedTextLayer, () => {
  nextTick(() => {
    renderCanvas()
  })
}, { deep: true })

watch(selectedImageLayer, () => {
  nextTick(() => {
    renderCanvas()
  })
}, { deep: true })
</script>

<style scoped>
.workspace-tab {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--base-color);
}

/* Comp List View */
.comp-list-view {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.comp-list-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 12px 16px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.comp-header-info {
  flex: 1;
}

.comp-header-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--text-color);
}

.comps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.comp-card {
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.comp-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(24, 160, 88, 0.2);
  transform: translateY(-2px);
}

.comp-card-preview {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
}

.comp-preview-placeholder {
  color: var(--text-color-2);
  font-size: 14px;
}

.comp-card {
  position: relative;
  cursor: default;
}

.comp-card-preview {
  cursor: pointer;
}

.comp-card-info {
  padding: 12px;
}

.comp-name-editable {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.comp-name-editable:hover {
  background-color: var(--hover-color);
}

.comp-name-input {
  margin-bottom: 4px;
}

.comp-name-input .n-input__input-el {
  font-size: 14px;
  font-weight: 600;
}

.comp-card-info h4 {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.comp-date {
  margin: 0;
  font-size: 12px;
  color: var(--text-color-2);
}

.comp-card-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.comp-card:hover .comp-card-actions {
  opacity: 1;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

/* Two Column Layout */
.workspace-two-column {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  align-items: flex-start;
}

@media (max-width: 1200px) {
  .workspace-two-column {
    grid-template-columns: 1fr;
  }
}

.comps-column {
  min-width: 0;
}

.deliverables-column {
  min-width: 0;
}

.deliverables-card {
  position: sticky;
  top: 24px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
}

.deliverables-header {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
}

.deliverables-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.deliverable-item {
  padding: 16px 0;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.2s ease;
}

.deliverable-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.deliverable-item:hover {
  background-color: var(--base-color);
  padding-left: 8px;
  padding-right: 8px;
  margin-left: -8px;
  margin-right: -8px;
  border-radius: 4px;
}

.deliverable-title {
  margin: 0 0 6px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.deliverable-meta {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--text-color-2);
}

/* Assets Manager Section */
.assets-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
}

/* Comp Edit View */
.workspace-main {
  flex: 1;
  display: flex;
  gap: 16px;
  padding: 16px 24px 24px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

/* Canvas Area (2/3 width) */
.canvas-area {
  flex: 2;
  display: flex;
  flex-direction: column;
  background: var(--card-color);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.canvas-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  background: #f5f5f5;
  background-image: 
    linear-gradient(45deg, #e0e0e0 25%, transparent 25%), 
    linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%), 
    linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  overflow: hidden;
}

.workspace-canvas {
  border: 2px solid #ddd;
  border-radius: 4px;
  cursor: crosshair;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  /* Ensure canvas preserves transparency */
  image-rendering: auto;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.canvas-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-family: monospace;
}

/* Controls Panel (1/3 width) */
.controls-panel {
  flex: 1;
  min-width: 300px;
  max-width: 400px;
}

/* Aspect Ratio Controls */
.aspect-ratio-controls {
  margin-bottom: 16px;
}

.aspect-ratio-controls .n-button-group {
  width: 100%;
  display: flex;
}

.aspect-ratio-controls .n-button-group .n-button {
  flex: 1;
}

/* Layer Controls */
.layer-controls {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.layer-buttons {
  width: 100%;
}

.layer-buttons .n-button-group {
  width: 100%;
  display: flex;
}

.layer-buttons .n-button-group .n-button {
  flex: 1;
}

.layer-details {
  margin-top: 8px;
}

.image-layer-details,
.text-layer-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}


.text-layer-details .copy-field {
  width: 100%;
}

.text-settings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.text-settings-grid .n-form-item {
  margin-bottom: 0;
}

.alignment-visible-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.alignment-visible-controls .n-button-group {
  flex: 1;
}

.transform-controls {
  display: flex;
  gap: 8px;
}

.asset-selector {
  width: 100%;
}

.selected-asset {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.selected-asset img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.asset-placeholder-small {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-color-2);
}

.asset-info-small {
  flex: 1;
}

.asset-info-small p {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
}

.no-asset-placeholder {
  width: 100%;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Asset Selector Modal */
.asset-selector-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.asset-selector-item {
  background: var(--card-color);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.asset-selector-item:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.asset-selector-item.selected {
  border-color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.asset-selector-preview {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.asset-selector-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.asset-selector-name {
  margin: 8px;
  font-size: 12px;
  color: var(--text-color);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Inline Asset Grid */
.asset-grid-inline {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.asset-item-inline {
  background: var(--card-color);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.asset-item-inline:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.asset-item-inline.selected {
  border-color: var(--primary-color);
  border-width: 3px;
  box-shadow: 0 0 0 2px rgba(24, 160, 88, 0.2);
}

.asset-preview-inline {
  width: 100%;
  aspect-ratio: 1;
  background: var(--base-color);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.asset-preview-inline img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.asset-placeholder-inline {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-2);
}

.asset-name-inline {
  margin: 4px;
  font-size: 11px;
  color: var(--text-color);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px;
}

.loading-state-small {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.empty-assets {
  padding: 20px;
  text-align: center;
  color: var(--text-color-2);
  font-size: 14px;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .workspace-main {
    flex-direction: column;
  }
  
  .canvas-area {
    flex: none;
    height: 60vh;
  }
  
  .controls-panel {
    flex: none;
    max-width: none;
  }
}
</style>

